import {AccountCenter,AccountPolicySettings,SystemAccountNav} from "./AccountCenter";
import {AccountRuntime,useAccountFeatures} from "./AccountRuntime";
import {AccountLogin} from "./AccountLogin";
import {useSessionPresence} from "./SessionPresence";
import {ExternalConnections} from "./ExternalConnections";
import {WorkerCopyModal} from "./WorkerCopy";
import {WorkerAvailability} from "./WorkerAvailability";
import {WorkerOperationsGuide} from "./WorkerOperations";
import {TaskFeed} from "./TaskFeed";
import {
  type FormEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  Boxes,
  Braces,
  Check,
  ChevronRight,
  CircleGauge,
  Clipboard,
  Cloud,
  Code2,
  Cpu,
  Database,
  Download,
  Eye,
  FolderOpen,
  HardDrive,
  History,
  KeyRound,
  Languages,
  LayoutDashboard,
  ListChecks,
  LogOut,
  Menu,
  MemoryStick,
  Moon,
  PanelLeftClose,
  PanelLeftOpen,
  PanelsTopLeft,
  Pencil,
  Plus,
  RefreshCw,
  ServerCog,
  Settings2,
  ShieldCheck,
  Sun,
  Trash2,
  UploadCloud,
  UserRound,
  Users,
  X,
  type LucideIcon,
} from "lucide-react";
import {advanceSessionGeneration, ApiError, api, patch, post, put} from "./api";
import type {Artifact, Controller, CurrentUser, Manager, Spec, StorageInitialization, StorageObject, StorageSettings, Summary, Task, TelemetryResponse, Worker, WorkerDetail} from "./types";
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  InlineNotice,
  Modal,
  PageErrorBoundary,
  PageHeader,
  PageLoading,
  StatusBadge,
  TableFrame,
  cx,
} from "./components/ui";
import {useI18n} from "./i18n";
import {controllerAgentPrompt, workerAgentPrompt} from "./agentPrompts";
import {DevelopmentPromptBuilder} from "./DevelopmentPromptBuilder";

type Route =
  | "dashboard"
  | "managers"
  | "workers"
  | "specs"
  | "controllers"
  | "tasks"
  | "settings"
  | "sessions"
  | "account"
  | "account-policy"
  | "webdav"
  | "storage"
  | "auth"
  | "api-docs"
  | "controller-app";

type NavigationItem = {id: Route; label: string; icon: LucideIcon; children?: NavigationItem[]};
type NavigationSection = {label: string; items: NavigationItem[]};

const navigation: NavigationSection[] = [
  {label: "Workspace", items: [{id: "dashboard", label: "Overview", icon: LayoutDashboard}]},
  {
    label: "Execution",
    items: [
      {id: "managers", label: "Managers Registration", icon: ServerCog},
      {id: "specs", label: "Specs Registration", icon: Braces},
      {id: "workers", label: "Workers Management", icon: Cpu},
      {id: "controllers", label: "Controllers Management", icon: PanelsTopLeft},
    ],
  },
  {
    label: "Activity",
    items: [
      {id: "tasks", label: "Running Tasks", icon: ListChecks},
    ],
  },
  {
    label: "Configuration",
    items: [
      {id: "settings", label: "Settings", icon: Settings2},
      {id: "api-docs", label: "API Documentation", icon: Code2},
    ],
  },
];

const routeMeta = Object.fromEntries(
  navigation.flatMap((section) => section.items.flatMap((item) => [
    [item.id, item],
    ...(item.children ?? []).map((child) => [child.id, child]),
  ])),
) as Record<Route, NavigationItem>;
routeMeta["controller-app"] = {id: "controller-app", label: "Controller", icon: PanelsTopLeft};
routeMeta.sessions = {id:"sessions",label:"Login devices",icon:Users};
routeMeta.account={id:"account",label:"My account",icon:UserRound};
routeMeta["account-policy"]={id:"account-policy",label:"Account policies",icon:ShieldCheck};
routeMeta.storage = {id: "storage", label: "Storage", icon: Database};
routeMeta.webdav = {id:"webdav",label:"WebDAV",icon:FolderOpen};
routeMeta.auth = {id: "auth", label: "Authentication", icon: ShieldCheck};

const routeSet = new Set<Route>(Object.keys(routeMeta) as Route[]);

function currentRoute(): Route {
  const segment = location.pathname.split("/").filter(Boolean)[0];
  if (segment === "apps") return "controller-app";
  if (segment === "protocol") return "api-docs";
  const candidate = segment as Route | undefined;
  return candidate && routeSet.has(candidate) ? candidate : "dashboard";
}

function currentControllerID() {
  const [segment, id] = location.pathname.split("/").filter(Boolean);
  return segment === "apps" ? decodeURIComponent(id || "") : "";
}

function navigate(route: Route, controllerID = "") {
  const path = route === "dashboard" ? "/" : route === "controller-app" ? `/apps/${encodeURIComponent(controllerID)}` : `/${route}`;
  history.pushState({}, "", path);
  dispatchEvent(new PopStateEvent("popstate"));
}

function navigateToWorkers(managerID: string) {
  history.pushState({}, "", `/workers?manager=${encodeURIComponent(managerID)}`);
  dispatchEvent(new PopStateEvent("popstate"));
}

function navigateToTasks(worker: Worker) {
  history.pushState({}, "", `/tasks?manager=${encodeURIComponent(worker.manager_id)}&worker=${encodeURIComponent(worker.id)}`);
  dispatchEvent(new PopStateEvent("popstate"));
}

function asList<T>(value: T[] | null | undefined): T[] {
  return Array.isArray(value) ? value : [];
}

function errorMessage(error: unknown) {
  return error instanceof Error ? error.message : "The request could not be completed.";
}

function formatDate(value?: string | null) {
  if (!value) return document.documentElement.lang.startsWith("zh") ? "从未" : "Never";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : new Intl.DateTimeFormat(document.documentElement.lang || undefined, {dateStyle: "medium", timeStyle: "short"}).format(date);
}

function formatBytes(value?: number | null) {
  const bytes = Number(value || 0);
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 ** 3) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
}

function formatPercent(value?: number | null) {
  return typeof value === "number" && Number.isFinite(value) ? `${value.toFixed(1)}%` : "—";
}

function formatDuration(value?: number | null) {
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0) return "—";
  const days = Math.floor(value / 86400);
  const hours = Math.floor((value % 86400) / 3600);
  const minutes = Math.floor((value % 3600) / 60);
  return [days && `${days}d`, (days || hours) && `${hours}h`, `${minutes}m`].filter(Boolean).join(" ");
}

function initials(value: string) {
  const parts = value.trim().split(/\s+/).filter(Boolean);
  return (parts.length > 1 ? `${parts[0][0]}${parts.at(-1)?.[0] ?? ""}` : value.slice(0, 2)).toUpperCase();
}

export function App() {
  const {t} = useI18n();
  const [route, setRoute] = useState<Route>(currentRoute());
  const [controllerID, setControllerID] = useState(currentControllerID());
  const [authenticated, setAuthenticated] = useState<boolean | null>(null);
  const [sidebarControllers, setSidebarControllers] = useState<Controller[]>([]);
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);
  const [bootError,setBootError]=useState("");
  const [sessionNotice,setSessionNotice]=useState("");
  useSessionPresence(authenticated);
  useEffect(()=>{
    const expired=(event:Event)=>{
      const reason=(event as CustomEvent<{reason:string}>).detail?.reason;
      setSessionNotice(reason==="logout"?"":reason==="password_changed"?"Your password changed on another device. Sign in again.":reason==="remote_logout"?"This device was signed out remotely. Sign in again.":"Your session expired. Sign in again.");
      setCurrentUser(null);setAuthenticated(false);
    };
    addEventListener("layla:session-expired",expired);return()=>removeEventListener("layla:session-expired",expired);
  },[]);
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dark, setDark] = useState(() => {
    try {
      const saved = localStorage.getItem("layla-theme");
      return saved&&saved!=="system" ? saved === "dark" : matchMedia("(prefers-color-scheme: dark)").matches;
    } catch {
      return false;
    }
  });

  useEffect(() => {
    const listener = () => {
      setRoute(currentRoute());
      setControllerID(currentControllerID());
    };
    addEventListener("popstate", listener);
    return () => removeEventListener("popstate", listener);
  }, []);

  const loadSidebarControllers = useCallback(async () => {
    if (!authenticated) return;
    const response = await api<{items: Controller[] | null}>("/controllers");
    setSidebarControllers(asList(response.items).filter((controller) => Boolean(controller.enabled)));
  }, [authenticated]);

  useEffect(() => {
    if (!authenticated) {
      setSidebarControllers([]);
      return;
    }
    void loadSidebarControllers().catch(() => undefined);
    const listener = () => void loadSidebarControllers().catch(() => undefined);
    addEventListener("layla:controllers-changed", listener);
    return () => removeEventListener("layla:controllers-changed", listener);
  }, [authenticated, loadSidebarControllers]);

  useEffect(() => {
    api<CurrentUser>("/me").then(user=>{setCurrentUser(user);setAuthenticated(true);}).catch(error=>{if(error instanceof ApiError&&error.status===401)setAuthenticated(false);else setBootError(String(error));});
  }, []);

  useEffect(() => {
    const openProfile = () => navigate("account");
    addEventListener("layla:open-profile", openProfile);
    return () => removeEventListener("layla:open-profile", openProfile);
  }, []);

  const loadCurrentUser = useCallback(async () => {
    const user = await api<CurrentUser>("/me");
    setCurrentUser(user);
  }, []);

  useEffect(() => {
    if (authenticated) void loadCurrentUser().catch(() => undefined);
    if (!authenticated) setCurrentUser(null);
  }, [authenticated, loadCurrentUser]);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    try {
      if(localStorage.getItem("layla-theme")!=="system")localStorage.setItem("layla-theme", dark ? "dark" : "light");
    } catch {
      // A blocked preference store should never prevent the console from rendering.
    }
  }, [dark]);

  function go(routeId: Route, selectedControllerID = "") {
    setMobileOpen(false);
    navigate(routeId, selectedControllerID);
  }

  function signedOut() {
    setCurrentUser(null);
    setAuthenticated(false);
  }

  useEffect(()=>{const changed=()=>{if(authenticated)void loadCurrentUser().catch(()=>undefined);};const theme=(e:Event)=>{const value=(e as CustomEvent<string>).detail;setDark(value==="dark"||value==="system"&&matchMedia("(prefers-color-scheme: dark)").matches);};addEventListener("layla:account-updated",changed);addEventListener("layla:theme",theme);document.documentElement.dataset.density=localStorage.getItem("layla-density")||"comfortable";return()=>{removeEventListener("layla:account-updated",changed);removeEventListener("layla:theme",theme);};},[authenticated,loadCurrentUser]);
  if (authenticated === null) return bootError?<ErrorState message={bootError} onRetry={()=>location.reload()}/>:<BootScreen />;
  if (!authenticated) return <AccountLogin notice={sessionNotice} onSuccess={(recovered) => {advanceSessionGeneration();setSessionNotice("");setAuthenticated(true); go(recovered||route==="account"?"account":"dashboard");}} />;

  return (
    <AccountRuntime><div className={cx("app-shell", collapsed && "sidebar-collapsed")}>
      <Sidebar route={route} controllerID={controllerID} controllers={sidebarControllers} collapsed={collapsed} user={currentUser} onNavigate={go} onOpenProfile={() => go("account")} onSignOut={signedOut} />
      {mobileOpen && (
        <div className="mobile-sheet" role="dialog" aria-modal="true" aria-label={t("Navigation")}>
          <button className="sheet-overlay" aria-label={t("Close navigation")} onClick={() => setMobileOpen(false)} />
          <div className="sheet-panel">
            <Button className="sheet-close" size="icon" variant="ghost" aria-label={t("Close navigation")} onClick={() => setMobileOpen(false)}>
              <X />
            </Button>
            <Sidebar route={route} controllerID={controllerID} controllers={sidebarControllers} collapsed={false} mobile user={currentUser} onNavigate={go} onOpenProfile={() => go("account")} onSignOut={signedOut} />
          </div>
        </div>
      )}
      <div className="app-column">
        <TopBar
          route={route}
          controllerName={sidebarControllers.find((controller) => controller.id === controllerID)?.name}
          dark={dark}
          collapsed={collapsed}
          onToggleTheme={() => {localStorage.setItem("layla-theme",dark?"light":"dark");setDark((value) => !value);}}
          onToggleSidebar={() => setCollapsed((value) => !value)}
          onOpenMobile={() => setMobileOpen(true)}
        />
        <main className="main-content">
          <PageErrorBoundary resetKey={route}>
            <Page route={route} controllerID={controllerID} />
          </PageErrorBoundary>
        </main>
      </div>
    </div></AccountRuntime>
  );
}

function BootScreen() {
  const {t} = useI18n();
  return (
    <div className="boot-screen">
      <span className="brand-box"><Boxes /></span>
      <div>
        <strong>LAYLA TAB</strong>
        <span>{t("Connecting to the control plane…")}</span>
      </div>
    </div>
  );
}

function Sidebar({
  route,
  controllerID,
  controllers,
  collapsed,
  mobile = false,
  user,
  onNavigate,
  onOpenProfile,
  onSignOut,
}: {
  route: Route;
  controllerID: string;
  controllers: Controller[];
  collapsed: boolean;
  mobile?: boolean;
  user: CurrentUser | null;
  onNavigate: (route: Route, controllerID?: string) => void;
  onOpenProfile: () => void;
  onSignOut: () => void;
}) {
  const {t} = useI18n();

  const [logoutError,setLogoutError]=useState("");
  async function signOut() {
    setLogoutError("");
    try {await api("/logout", {method:"POST"});onSignOut();}
    catch(e){setLogoutError(errorMessage(e));}
  }

  const renderNavigationSection = (section: NavigationSection) => (
    <div className="nav-section" key={section.label}>
      {!collapsed && <div className="nav-label">{t(section.label)}</div>}
      {section.items.map(({id, label, icon: Icon}) => {
        const active = route === id || (id === "settings" && ["storage", "auth", "webdav","account-policy"].includes(route));
        return <div className="nav-group" key={id}>
          <button
            className={cx("nav-item", active && "active")}
            aria-current={active ? "page" : undefined}
            title={collapsed ? t(label) : undefined}
            onClick={() => onNavigate(id)}
          >
            <Icon aria-hidden="true" />
            {!collapsed && <span>{t(label)}</span>}
            {!collapsed && active && <ChevronRight className="nav-chevron" aria-hidden="true" />}
          </button>
        </div>;
      })}
    </div>
  );

  return (
    <aside className={cx("sidebar", mobile && "sidebar-mobile")}>
      <button className="brand" onClick={() => onNavigate("dashboard")} aria-label={t("Open overview")}>
        <span className="brand-box"><Boxes /></span>
        {!collapsed && <span className="brand-copy"><strong>LAYLA TAB</strong><small>CONTROL PLANE</small></span>}
      </button>
      <nav className="sidebar-nav" aria-label={t("Primary navigation")}>
        {navigation.filter((section) => section.label !== "Configuration").map(renderNavigationSection)}
        {controllers.length > 0 && (
          <div className="nav-section controller-nav-section">
            {!collapsed && <div className="nav-label">{t("Functions")}</div>}
            {controllers.map((controller) => (
              <button
                className={cx("nav-item", route === "controller-app" && controllerID === controller.id && "active")}
                key={controller.id}
                title={collapsed ? (controller.alias || controller.name) : undefined}
                onClick={() => onNavigate("controller-app", controller.id)}
              >
                <PanelsTopLeft aria-hidden="true" />
                {!collapsed && <span>{controller.alias || controller.name}</span>}
                {!collapsed && route === "controller-app" && controllerID === controller.id && <ChevronRight className="nav-chevron" aria-hidden="true" />}
              </button>
            ))}
          </div>
        )}
        {navigation.filter((section) => section.label === "Configuration").map(renderNavigationSection)}
      </nav>
      <div className="sidebar-footer">
        <button className="account-row account-button" title={collapsed ? (user?.display_name || t("Administrator")) : undefined} aria-label={t("Open account settings")} onClick={onOpenProfile}>
          <span className="avatar">{user?.avatar?<img src={`/api/ui/v1/me/account/avatar?v=${user.avatar_revision}`} alt="本人头像"/>:initials(user?.display_name || user?.username || "Administrator")}</span>
          {!collapsed && <span><strong>{user?.display_name || t("Administrator")}</strong><small>{t("My account")} · {user?.username}</small></span>}
          {!collapsed && <ChevronRight />}
        </button>
        {logoutError&&<InlineNotice tone="error">{logoutError}</InlineNotice>}<button className="nav-item" title={collapsed ? t("Sign out") : undefined} onClick={signOut}>
          <LogOut aria-hidden="true" />
          {!collapsed && <span>{t("Sign out")}</span>}
        </button>
      </div>
    </aside>
  );
}

function TopBar({
  route,
  controllerName,
  dark,
  collapsed,
  onToggleTheme,
  onToggleSidebar,
  onOpenMobile,
}: {
  route: Route;
  controllerName?: string;
  dark: boolean;
  collapsed: boolean;
  onToggleTheme: () => void;
  onToggleSidebar: () => void;
  onOpenMobile: () => void;
}) {
  const {t} = useI18n();
  const features=useAccountFeatures();
  const title = route === "controller-app" ? controllerName || t("Controller") : t(routeMeta[route]?.label || "Platform");
  return (
    <header className="topbar">
      <div className="topbar-title">
        <Button className="desktop-toggle" size="icon" variant="ghost" aria-label={t(collapsed ? "Expand sidebar" : "Collapse sidebar")} onClick={onToggleSidebar}>
          {collapsed ? <PanelLeftOpen /> : <PanelLeftClose />}
        </Button>
        <Button className="mobile-toggle" size="icon" variant="ghost" aria-label={t("Open navigation")} onClick={onOpenMobile}>
          <Menu />
        </Button>
        <h1>{title}</h1>
      </div>
      <div className="topbar-actions">
        <button className="protocol-pill" onClick={() => {history.pushState({}, "", "/api-docs?view=protocols"); dispatchEvent(new PopStateEvent("popstate")); window.setTimeout(() => dispatchEvent(new Event("layla:open-protocol")), 0);}}><CircleGauge /> {t("Protocol v1")}</button>
        {features.preferences&&<Button size="icon" variant="ghost" aria-label={t(dark ? "Use light theme" : "Use dark theme")} onClick={onToggleTheme}>
          {dark ? <Sun /> : <Moon />}
        </Button>}
      </div>
    </header>
  );
}

function Page({route, controllerID}: {route: Route; controllerID: string}) {
  switch (route) {
    case "managers": return <Managers />;
    case "workers": return <Workers />;
    case "specs": return <Specs />;
    case "controllers": return <Controllers />;
    case "tasks": return <GroupedTasks />;
    case "webdav": return <><SystemAccountNav/><ExternalConnections /></>;
    case "settings": return <Settings />;
    case "sessions": return <AccountCenter initialTab="devices"/>;
    case "account": return <AccountCenter/>;
    case "account-policy": return <AccountPolicySettings/>;
    case "storage": return <Storage />;
    case "auth": return <Authentication />;
    case "api-docs": return <ApiDocumentation />;
    case "controller-app": return <ControllerApplication controllerID={controllerID} />;
    default: return <Dashboard />;
  }
}

function useLoad<T>(path: string, initial: T) {
  const [data, setData] = useState(initial);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setData(await api<T>(path));
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setLoading(false);
    }
  }, [path]);
  useEffect(() => {void load();}, [load]);
  return {data, error, loading, load};
}

function RefreshButton({onClick}: {onClick: () => void}) {
  const {t} = useI18n();
  return <Button size="icon" aria-label={t("Refresh")} title={t("Refresh")} onClick={onClick}><RefreshCw /></Button>;
}

function Dashboard() {
  const {t} = useI18n();
  const {data, error, loading, load} = useLoad<Summary>("/summary", {
    managers: 0,
    workers: 0,
    specs: 0,
    controllers: 0,
    tasks: 0,
    artifacts: 0,
    storage_ready: false,
  });
  const stats = [
    [t("Managers"), data.managers, ServerCog],
    [t("Workers"), data.workers, Cpu],
    [t("Specs"), data.specs, Braces],
    [t("Controllers"), data.controllers, PanelsTopLeft],
    [t("Tasks"), data.tasks, ListChecks],
  ] as const;

  return (
    <>
      <PageHeader title={t("Overview")} description={t("A live summary of the Layla execution control plane.")} actions={<RefreshButton onClick={load} />} />
      {error ? <ErrorState message={error} onRetry={load} /> : loading ? <PageLoading /> : (
        <>
          <div className="stat-grid">
            {stats.map(([label, value, Icon]) => (
              <Card className="stat-card" key={label}>
                <div><span>{label}</span><strong>{value}</strong></div>
                <Icon aria-hidden="true" />
              </Card>
            ))}
          </div>
          <Card title={t("Architecture health")} description={t("Platform readiness and optional extension status.")}>
            <div className="health-grid">
              <Health icon={Boxes} title={t("Platform Core")} detail={t("Ready without extensions")} tone="success" />
              <Health icon={Cloud} title={t("Artifact Storage")} detail={t(data.storage_ready ? "S3 adapter ready" : "Configuration required")} tone={data.storage_ready ? "success" : "warning"} />
              <Health icon={Braces} title={t("Protocol Registry")} detail="Platform Protocol v1" tone="info" />
            </div>
          </Card>
        </>
      )}
    </>
  );
}

function Health({icon: Icon, title, detail, tone}: {icon: LucideIcon; title: string; detail: string; tone: "success" | "warning" | "info"}) {
  return (
    <div className="health-item">
      <span className={cx("object-icon", `object-${tone}`)}><Icon /></span>
      <span><strong>{title}</strong><small>{detail}</small></span>
    </div>
  );
}

type ManagerCredential = {manager_id: string; manager_key: string; name: string};
type WorkerCredential = {worker_id: string; manager_id: string; name: string; spec_name: string};

function Managers() {
  const {t} = useI18n();
  const {data, error, loading, load} = useLoad<{managers: Manager[] | null}>("/managers", {managers: []});
  const managers = asList(data?.managers);
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<ManagerCredential | null>(null);
  const [selectedManager, setSelectedManager] = useState<Manager | null>(null);
  const [editingManager, setEditingManager] = useState<Manager | null>(null);
  const [deleting, setDeleting] = useState<Manager | null>(null);
  const [actionError, setActionError] = useState("");

  async function create(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setActionError("");
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    try {
      const credential = await post<ManagerCredential>("/managers", {name: form.get("name")});
      setCreated(credential);
      setCreating(false);
      formElement.reset();
      await load();
    } catch (requestError) {
      setActionError(errorMessage(requestError));
    }
  }

  async function setEnabled(manager: Manager) {
    try {
      await patch(`/managers/${manager.id}`, {enabled: !manager.enabled});
      await load();
    } catch (requestError) {
      setActionError(errorMessage(requestError));
    }
  }

  async function saveManager(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!editingManager) return;
    setActionError("");
    const form = new FormData(event.currentTarget);
    const name = String(form.get("name") || "").trim();
    const parallelLimit = Number(form.get("parallel_limit"));
    try {
      await patch(`/managers/${encodeURIComponent(editingManager.id)}`, {name,parallel_limit:parallelLimit});
      if (selectedManager?.id === editingManager.id) setSelectedManager({...selectedManager, name});
      setEditingManager(null);
      await load();
    } catch (requestError) {
      setActionError(errorMessage(requestError));
    }
  }

  async function removeManager() {
    if (!deleting) return;
    setActionError("");
    try {
      await api(`/managers/${encodeURIComponent(deleting.id)}`, {method: "DELETE"});
      if (selectedManager?.id === deleting.id) setSelectedManager(null);
      setDeleting(null);
      await load();
    } catch (requestError) {
      setActionError(errorMessage(requestError));
    }
  }

  return (
    <>
      <PageHeader title={t("Managers")} description={t("Register and control the hosts that coordinate local Workers.")} actions={<><RefreshButton onClick={load} /><Button variant="primary" onClick={() => setCreating(true)}><Plus />{t("New manager")}</Button></>} />
      <Modal open={creating} title={t("Register Manager")} description={t("The Manager Key is shown once after creation.")} onClose={() => setCreating(false)}>
        <form className="compact-form" onSubmit={create}><Field name="name" label={t("Manager name")} placeholder={t("Production executor")} required /><div className="form-actions"><Button onClick={() => setCreating(false)}>{t("Cancel")}</Button><Button variant="primary" type="submit">{t("Create manager")}</Button></div></form>
      </Modal>
      {selectedManager && <ManagerDetailsModal manager={selectedManager} onClose={() => setSelectedManager(null)} />}
      <Modal open={editingManager !== null} title={`${t("Edit Manager")} · ${editingManager?.name || ""}`} description={t("Configure the display name and node execution capacity.")} onClose={() => setEditingManager(null)}>
        {editingManager && <form className="compact-form" onSubmit={saveManager}>
          <Field name="name" label={t("Manager display name")} value={editingManager.name} required /><label className="field"><span>{t("Parallel concurrency")}</span><input name="parallel_limit" type="number" min="1" max="64" defaultValue={editingManager.parallel_limit??10} readOnly={Boolean(editingManager.host?.execution?.config_source_profile&&editingManager.host.execution.reported_profile&&editingManager.host.execution.config_source_profile!==editingManager.host.execution.reported_profile)} required /></label><InlineNotice>{editingManager.host?.execution?.config_source_profile&&editingManager.host.execution.reported_profile&&editingManager.host.execution.config_source_profile!==editingManager.host.execution.reported_profile?t("This node takes execution settings from another profile."):t("Serial capacity is fixed at 1. Parallel tasks use an independent lane. Changes apply on heartbeat; running tasks finish normally.")}</InlineNotice>
          <InlineNotice>{t("This name is presentation-only. Manager authentication and connectivity are unchanged.")}</InlineNotice>
          <div className="form-actions"><Button onClick={() => setEditingManager(null)}>{t("Cancel")}</Button><Button variant="primary" type="submit"><Pencil />{t("Save changes")}</Button></div>
        </form>}
      </Modal>
      <Modal
        open={deleting !== null}
        title={t("Delete Manager")}
        description={t("The Manager Key will stop working immediately. Task history is retained.")}
        onClose={() => setDeleting(null)}
        footer={<><Button onClick={() => setDeleting(null)}>{t("Cancel")}</Button><Button variant="danger" onClick={() => void removeManager()}><Trash2 />{t("Confirm deletion")}</Button></>}
      >
        <InlineNotice tone="warning">{t("Workers registered under this Manager will be disabled and disconnected. This action cannot be undone.")}</InlineNotice>
        {deleting && <div className="delete-summary"><strong>{deleting.name}</strong><code>{deleting.id}</code></div>}
      </Modal>
      {created && <InlineNotice tone="warning"><strong>{t("Save this Manager Key now.")}</strong><code>{created.manager_key}</code><Button size="sm" onClick={() => navigator.clipboard.writeText(created.manager_key)}><Clipboard />{t("Copy")}</Button></InlineNotice>}
      {actionError && <InlineNotice tone="error">{actionError}</InlineNotice>}
      {error ? <ErrorState message={error} onRetry={load} /> : loading ? <PageLoading /> : managers.length === 0 ? (
        <Card><EmptyState title={t("No Managers yet")} description={t("The Platform remains operational. Register a Manager when an execution host is ready.")} action={<Button variant="primary" onClick={() => setCreating(true)}><Plus />{t("New manager")}</Button>} /></Card>
      ) : (
        <TableFrame><table><thead><tr><th>{t("Name")}</th><th>{t("Machine")}</th><th>{t("Status")}</th><th>{t("Last heartbeat")}</th><th className="align-right">{t("Actions")}</th></tr></thead><tbody>{managers.map((manager) => <tr key={manager.id} className="clickable-row" onClick={() => setSelectedManager(manager)}><td className="name-cell"><ServerCog /> <span><strong>{manager.name}</strong><small><code>{manager.id}</code></small></span></td><td><strong>{manager.host?.hostname || t("Waiting for heartbeat")}</strong><small>{[manager.host?.os, manager.host?.arch].filter(Boolean).join(" / ") || "—"}</small></td><td><StatusBadge value={manager.enabled ? "enabled" : "disabled"} /><small>{t("Serial")} {manager.host?.execution?.serial_running??0}/1 · {t("Parallel")} {manager.host?.execution?.parallel_running??0}/{manager.host?.execution?.parallel_limit??manager.parallel_limit??10}</small>{manager.host?.execution?.config_source_profile&&<small>{t("Configuration source")}: {manager.host.execution.config_source_profile}</small>}</td><td className="muted-cell">{formatDate(manager.last_heartbeat_at)}</td><td className="align-right"><div className="manager-actions"><Button size="sm" onClick={(event) => {event.stopPropagation(); navigateToWorkers(manager.id);}}><Users />{t("View Workers")}</Button><Button size="sm" onClick={(event) => {event.stopPropagation(); setEditingManager(manager);}}><Pencil />{t("Edit")}</Button><Button size="sm" onClick={(event) => {event.stopPropagation(); setSelectedManager(manager);}}><Eye />{t("View")}</Button><Button size="sm" onClick={(event) => {event.stopPropagation(); void setEnabled(manager);}}>{t(manager.enabled ? "Disable" : "Enable")}</Button><Button size="sm" variant="danger" onClick={(event) => {event.stopPropagation(); setDeleting(manager);}}><Trash2 />{t("Delete")}</Button></div></td></tr>)}</tbody></table></TableFrame>
      )}
    </>
  );
}

function MetricCard({icon: Icon, label, value, detail, percent}: {icon: LucideIcon; label: string; value: string; detail: string; percent?: number}) {
  const width = typeof percent === "number" && Number.isFinite(percent) ? Math.max(0, Math.min(100, percent)) : 0;
  return (
    <div className="machine-metric">
      <div className="machine-metric-heading"><span className="object-icon object-info"><Icon /></span><span>{label}</span></div>
      <strong>{value}</strong>
      <small>{detail}</small>
      <span className="progress-track"><span style={{width: `${width}%`}} /></span>
    </div>
  );
}

function ManagerDetailsModal({manager, onClose}: {manager: Manager; onClose: () => void}) {
  const {t} = useI18n();
  const {data: detail, error, loading, load} = useLoad<Manager>(`/managers/${encodeURIComponent(manager.id)}`, manager);
  const {data: telemetry, error: telemetryError, loading: telemetryLoading, load: loadTelemetry} = useLoad<TelemetryResponse>(`/managers/${encodeURIComponent(manager.id)}/telemetry`, {latest: null, samples: []});
  const [showKey, setShowKey] = useState(false);
  const host = detail.host ?? {};
  const loadAverage = [host.load_1, host.load_5, host.load_15].every((value) => typeof value === "number")
    ? `${host.load_1} / ${host.load_5} / ${host.load_15}`
    : "—";
  return (
    <Modal open title={`${t("Machine details")} · ${manager.name}`} description={t("Live host information reported by this Manager heartbeat.")} onClose={onClose}>
      {error ? <ErrorState message={error} onRetry={load} /> : loading ? <PageLoading /> : <div className="machine-overview">
        <div className="machine-identity"><span className="object-icon object-info"><ServerCog /></span><div><strong>{host.hostname || t("Waiting for heartbeat")}</strong><small>{detail.manager_id || detail.id || manager.id}</small></div><StatusBadge value={detail.enabled ? "enabled" : "disabled"} /></div>
        <div className="credential-panel">
          <div><span>{t("Manager Key")}</span><code>{showKey ? detail.manager_key : "••••••••••••••••••••••••••••••••"}</code></div>
          <div className="inline-controls"><Button size="sm" onClick={() => setShowKey((value) => !value)}><Eye />{t(showKey ? "Hide" : "Reveal")}</Button><Button size="sm" disabled={!detail.manager_key} onClick={() => navigator.clipboard.writeText(detail.manager_key || "")}><Clipboard />{t("Copy")}</Button></div>
        </div>
        <div className="machine-metrics">
          <MetricCard icon={Cpu} label={t("CPU usage")} value={formatPercent(host.cpu_usage_percent)} detail={`${host.cpu_count ?? "—"} ${t("logical CPUs")}`} percent={host.cpu_usage_percent} />
          <MetricCard icon={MemoryStick} label={t("Memory usage")} value={formatPercent(host.memory_usage_percent)} detail={`${formatBytes(host.memory_used_bytes)} / ${formatBytes(host.memory_total_bytes)}`} percent={host.memory_usage_percent} />
          <MetricCard icon={HardDrive} label={t("Disk usage")} value={formatPercent(host.disk_usage_percent)} detail={`${formatBytes(host.disk_used_bytes)} / ${formatBytes(host.disk_total_bytes)}`} percent={host.disk_usage_percent} />
        </div>
        <div className="key-value-list machine-details">
          <div><span>{t("Operating system")}</span><strong>{host.os || "—"} {host.arch || ""}</strong></div>
          <div><span>{t("Manager version")}</span><strong>{host.manager_version || "—"}</strong></div>
          <div><span>{t("System uptime")}</span><strong>{formatDuration(host.uptime_seconds)}</strong></div>
          <div><span>{t("Load average")}</span><strong>{loadAverage}</strong></div>
          <div><span>{t("Disk mount")}</span><strong>{host.disk_path || "—"}</strong></div>
          <div><span>{t("Metrics collected")}</span><strong>{formatDate(host.collected_at)}</strong></div>
          <div><span>{t("Last heartbeat")}</span><strong>{formatDate(detail.last_heartbeat_at)}</strong></div>
        </div>
        <TelemetryPanel telemetry={telemetry} error={telemetryError} loading={telemetryLoading} onRetry={loadTelemetry} />
      </div>}
    </Modal>
  );
}

function Workers() {
  const {t} = useI18n();
  const {data, error, loading, load} = useLoad<{items: Worker[] | null}>("/workers", {items: []});
  const {data: managerData} = useLoad<{managers: Manager[] | null}>("/managers", {managers: []});
  const {data: specData} = useLoad<{specs: Spec[] | null}>("/specs", {specs: []});
  const workers = asList(data?.items);
  const managers = asList(managerData?.managers);
  const specs = asList(specData?.specs);
  const [managerFilter, setManagerFilter] = useState(() => new URLSearchParams(location.search).get("manager") || "");
  const [selected, setSelected] = useState<Worker | null>(null);
  const [creating, setCreating] = useState(false);
  const [copying,setCopying]=useState<Worker|null>(null);
  const {data:currentUser}=useLoad<CurrentUser>("/me",{role:"user"} as CurrentUser);
  const [created, setCreated] = useState<WorkerCredential | null>(null);
  const [actionError, setActionError] = useState("");
  const [deleting, setDeleting] = useState<Worker | null>(null);
  const [deleteError, setDeleteError] = useState("");
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [actionSuccess, setActionSuccess] = useState("");
  const filtered = managerFilter ? workers.filter((worker) => worker.manager_id === managerFilter) : workers;

  function changeManagerFilter(managerID: string) {
    setManagerFilter(managerID);
    const url = managerID ? `/workers?manager=${encodeURIComponent(managerID)}` : "/workers";
    history.replaceState({}, "", url);
  }

  async function setEnabled(worker: Worker) {
    setActionError("");
    try {
      await patch(`/workers/${worker.id}`, {enabled: !Boolean(worker.enabled)});
      await load();
      setActionSuccess(t(Boolean(worker.enabled) ? "New task intake stopped. Already submitted tasks continue." : "New task intake resumed."));
    } catch (requestError) {
      setActionError(errorMessage(requestError));
    }
  }

  async function createWorker(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setActionError("");
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    const managerID = String(form.get("manager_id") || "");
    try {
      const worker = await post<WorkerCredential>(`/managers/${encodeURIComponent(managerID)}/workers`, {name: form.get("name"), spec_name: form.get("spec_name")});
      setCreated(worker);
      setCreating(false);
      formElement.reset();
      await load();
    } catch (requestError) {
      setActionError(errorMessage(requestError));
    }
  }

  async function removeWorker() {
    if (!deleting || deleteBusy) return;
    setDeleteError("");
    setDeleteBusy(true);
    try {
      await api(`/workers/${encodeURIComponent(deleting.id)}`, {method: "DELETE"});
      if (selected?.id === deleting.id) setSelected(null);
      setDeleting(null);
      setActionSuccess(t("Worker deleted. Task records and files are retained."));
      await load();
    } catch (requestError) {
      if (requestError instanceof ApiError && requestError.code === "WORKER_IN_USE") {
        setDeleteError(t("This Worker has {{tasks}} unfinished Task(s). Complete or cancel them before deleting.").replace("{{tasks}}", String(requestError.details.tasks || 0)));
      } else setDeleteError(errorMessage(requestError));
    } finally { setDeleteBusy(false); }
  }

  return (
    <>
      <PageHeader title={t("Workers")} description={t("Manage Worker connections and new task intake. Already submitted tasks continue when intake is stopped.")} actions={<><RefreshButton onClick={load} /><label className="header-filter"><span>{t("Manager filter")}</span><select aria-label={t("Filter by Manager")} value={managerFilter} onChange={(event) => changeManagerFilter(event.target.value)}><option value="">{t("All Managers")}</option>{managers.map((manager) => <option key={manager.id} value={manager.id}>{manager.name}</option>)}</select></label><WorkerOperationsGuide /><Button variant="primary" onClick={() => setCreating(true)}><Plus />{t("New worker")}</Button></>} />
      <Modal open={creating} title={t("Register Worker")} description={t("Assign the Worker to a Manager and Spec. The Worker reports and controls its active version when it connects.")} onClose={() => setCreating(false)}>
        <form className="compact-form" onSubmit={createWorker}>
          <Field name="name" label={t("Worker name")} placeholder="MarkItDown Worker" required />
          <label className="field"><span>{t("Manager")}<b>*</b></span><select name="manager_id" required defaultValue=""><option value="" disabled>{t("Select Manager")}</option>{managers.map((manager) => <option key={manager.id} value={manager.id}>{manager.name}</option>)}</select></label>
          <label className="field"><span>{t("Spec")}<b>*</b></span><select name="spec_name" required defaultValue=""><option value="" disabled>{t("Select Spec")}</option>{specs.map((spec) => <option key={spec.name} value={spec.name}>{spec.name}</option>)}</select></label>
          <div className="form-actions"><Button onClick={() => setCreating(false)}>{t("Cancel")}</Button><Button variant="primary" type="submit">{t("Create worker")}</Button></div>
        </form>
      </Modal>
      <Modal open={deleting !== null} title={`${t("Delete Worker")} · ${deleting?.name || ""}`} description={t("Remove this Worker registration. Existing Task records and files are retained.")} onClose={() => {if (!deleteBusy) setDeleting(null);}} footer={<><Button disabled={deleteBusy} onClick={() => setDeleting(null)}>{t("Cancel")}</Button><Button variant="danger" disabled={deleteBusy} onClick={() => void removeWorker()}><Trash2 />{t(deleteBusy ? "Deleting…" : "Delete")}</Button></>}>
        {deleteError && <InlineNotice tone="error">{deleteError}</InlineNotice>}
        <InlineNotice tone="warning">{t("A Worker with unfinished Tasks cannot be deleted. To use it again after deletion, register a new Worker ID and update the Manager mapping.")}</InlineNotice>
        {deleting && <div className="delete-summary"><strong>{deleting.name}</strong><code>{deleting.id}</code><span>{deleting.spec_name}</span></div>}
      </Modal>
      {actionSuccess && <InlineNotice tone="success">{actionSuccess}</InlineNotice>}
      {copying && <WorkerCopyModal source={copying} owner={currentUser.username} managers={managers} onClose={()=>setCopying(null)} onChanged={()=>void load()}/>}
      {selected && <WorkerDetailsModal worker={selected} onClose={() => setSelected(null)} />}
      {created && <InlineNotice tone="success"><strong>{t("Worker created. Use this ID when starting the Worker:")}</strong><code>{created.worker_id}</code><Button size="sm" onClick={() => navigator.clipboard.writeText(created.worker_id)}><Clipboard />{t("Copy")}</Button></InlineNotice>}
      {actionError && <InlineNotice tone="error">{actionError}</InlineNotice>}
      {error ? <ErrorState message={error} onRetry={load} /> : loading ? <PageLoading /> : workers.length === 0 ? (
        <Card><EmptyState title={t("No Workers registered")} description={t("Workers appear here after they are assigned to a Manager and connect through the local runtime protocol.")} /></Card>
      ) : (
        <TableFrame><table><thead><tr><th>{t("Worker")}</th><th>{t("Manager")}</th><th>{t("Spec")}</th><th>{t("Runtime")}</th><th>{t("Version")}</th><th>{t("Status")}</th><th className="align-right">{t("Actions")}</th></tr></thead><tbody>{filtered.map((worker) => <tr key={worker.id}><td><button className="table-primary-action" onClick={() => setSelected(worker)}><strong>{worker.name}</strong><small><code>{worker.id}</code></small></button></td><td><strong>{worker.manager_name || worker.manager_id}</strong><small><code>{worker.manager_id}</code></small></td><td><strong>{worker.spec_name}</strong><small>{t(worker.execution_mode==="parallel"?"Parallel":"Serial")}</small><small><StatusBadge value={worker.spec_validation_state} /></small></td><td><strong>{worker.runtime_version || t("Not reported")}</strong><small><StatusBadge value={worker.connection_state} /></small></td><td><strong>{worker.active_spec_version || "—"}</strong><small>{t("Reported by Worker")}</small></td><td><WorkerAvailability worker={worker} /></td><td className="align-right"><div className="manager-actions">{currentUser?.role==="admin"&&<Button size="sm" disabled={!Boolean(worker.enabled)||worker.connection_state!=="online"} onClick={()=>setCopying(worker)}>{t("Copy Worker")}</Button>}<Button size="sm" onClick={() => navigateToTasks(worker)}><History />{t("Task history")}</Button><Button size="sm" onClick={() => setSelected(worker)}><Eye />{t("Details")}</Button><Button size="sm" onClick={() => void setEnabled(worker)}>{t(Boolean(worker.enabled) ? "Stop accepting tasks" : "Resume accepting tasks")}</Button><Button size="sm" variant="danger" onClick={() => {setDeleteError(""); setActionSuccess(""); setDeleting(worker);}}><Trash2 />{t("Delete")}</Button></div></td></tr>)}</tbody></table></TableFrame>
      )}
    </>
  );
}

function WorkerDetailsModal({worker, onClose}: {worker: Worker; onClose: () => void}) {
  const {t} = useI18n();
  const {data, error, loading, load} = useLoad<WorkerDetail>(`/workers/${encodeURIComponent(worker.id)}`, {worker, supported_specs: [], tasks: []});
  const detail = data.worker;
  const config = {
    worker_id: detail.id,
    manager_id: detail.manager_id,
    manager_name: detail.manager_name,
    spec_name: detail.spec_name,
    active_spec_version: detail.active_spec_version,
    runtime_version: detail.runtime_version,
    enabled: Boolean(detail.enabled),
    effective_enabled: Boolean(detail.effective_enabled),
    connection_state: detail.connection_state,
    spec_validation_state: detail.spec_validation_state,
  };
  return (
    <Modal open title={`${t("Worker details")} · ${worker.name}`} description={t("Runtime configuration and supported contracts reported by this Worker.")} onClose={onClose}>
      {error ? <ErrorState message={error} onRetry={load} /> : loading ? <PageLoading /> : <div className="detail-stack">
        <div className="key-value-list detail-grid"><InfoRow label={t("Worker ID")} value={detail.id} /><InfoRow label={t("Manager")} value={`${detail.manager_name || "—"} · ${detail.manager_id}`} /><InfoRow label={t("Runtime version")} value={detail.runtime_version || t("Not reported")} /><InfoRow label={t("Active Spec version")} value={detail.active_spec_version || "—"} /><InfoRow label={t("Last seen")} value={formatDate(detail.last_seen_at)} /><InfoRow label={t("Effective state")} value={t(Boolean(detail.effective_enabled) ? "enabled" : "disabled")} /></div>
        <section className="detail-section"><div className="section-heading"><h3>{t("Worker configuration")}</h3><p>{t("Configuration known to Platform and reported by the Worker runtime.")}</p></div><pre className="json-viewer">{JSON.stringify(config, null, 2)}</pre></section>
        <section className="detail-section"><div className="section-heading"><h3>{t("Supported Specs")}</h3><p>{t("The first accepted declaration is controlled by the Worker as its active version.")}</p></div>{data.supported_specs.length === 0 ? <EmptyState title={t("No supported Specs reported")} description={t("Start the Worker to publish its contract declarations.")} /> : <TableFrame><table><thead><tr><th>{t("Spec")}</th><th>{t("Version")}</th><th>{t("Digest")}</th><th>{t("Validation")}</th></tr></thead><tbody>{data.supported_specs.map((spec) => <tr key={`${spec.spec_name}-${spec.version}`}><td>{spec.spec_name}</td><td>{spec.version}</td><td><code>{spec.digest}</code></td><td><StatusBadge value={spec.validation_state} /></td></tr>)}</tbody></table></TableFrame>}</section>
      </div>}
    </Modal>
  );
}

function Specs() {
  const {t} = useI18n();
  const {data, error, loading, load} = useLoad<{specs: Spec[] | null}>("/specs", {specs: []});
  const specs = asList(data?.specs);
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState<Spec | null>(null);
  const [editing, setEditing] = useState<Spec | null>(null);
  const [deleting, setDeleting] = useState<Spec | null>(null);
  const [actionError, setActionError] = useState("");
  const [deleteError, setDeleteError] = useState("");
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [actionSuccess, setActionSuccess] = useState("");

  async function create(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setActionError("");
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    try {
      await post("/specs", {name: form.get("name"), description: form.get("description"),execution_mode:form.get("execution_mode")});
      setCreating(false);
      formElement.reset();
      await load();
    } catch (requestError) {
      setActionError(errorMessage(requestError));
    }
  }

  async function setEnabled(spec: Spec) {
    setActionError("");
    try {
      await patch(`/specs/${encodeURIComponent(spec.name)}`, {enabled: !spec.enabled});
      await load();
      if (selected?.name === spec.name) setSelected({...selected, enabled: !spec.enabled});
    } catch (requestError) {
      setActionError(errorMessage(requestError));
    }
  }

  async function saveDescription(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!editing) return;
    setActionError("");
    const form=new FormData(event.currentTarget);
    const description = String(form.get("description") || "");
    const execution_mode=String(form.get("execution_mode")||"serial");
    try {
      await patch(`/specs/${encodeURIComponent(editing.name)}`, {description,execution_mode});
      setEditing(null);
      await load();
    } catch (requestError) {
      setActionError(errorMessage(requestError));
    }
  }

  async function removeSpec() {
    if (!deleting || deleteBusy) return;
    setDeleteError("");
    setDeleteBusy(true);
    try {
      await api(`/specs/${encodeURIComponent(deleting.name)}`, {method: "DELETE"});
      if (selected?.name === deleting.name) setSelected(null);
      setDeleting(null);
      setActionSuccess(t("Spec family deleted."));
      await load();
    } catch (requestError) {
      if (requestError instanceof ApiError && requestError.code === "SPEC_IN_USE") {
        const workers = Number(requestError.details.workers || 0);
        const controllers = Number(requestError.details.controllers || 0);
        setDeleteError(t("This Spec is still referenced by {{workers}} Worker(s) and {{controllers}} Controller(s).").replace("{{workers}}", String(workers)).replace("{{controllers}}", String(controllers)) + " " + t("Remove the referencing Workers and Controllers first, then retry."));
      } else setDeleteError(errorMessage(requestError));
    } finally { setDeleteBusy(false); }
  }

  return (
    <>
      <PageHeader title={t("Spec Registry")} description={t("Versioned, immutable business contracts shared by Controllers and Workers.")} actions={<><RefreshButton onClick={load} /><Button variant="primary" onClick={() => setCreating(true)}><Plus />{t("New draft")}</Button></>} />
      <Modal open={creating} title={t("Create Spec family")} description={t("Create the stable identity before publishing immutable versions.")} onClose={() => setCreating(false)}>
        <form className="compact-form" onSubmit={create}>
          <Field name="name" label={t("Stable name")} placeholder={t("DocumentConversion")} required />
          <Field name="description" label={t("Description")} placeholder={t("What this capability provides")} /><label className="field"><span>{t("Execution mode")}</span><select name="execution_mode" defaultValue="serial"><option value="serial">{t("Serial")}</option><option value="parallel">{t("Parallel")}</option></select></label><InlineNotice>{t("This is a Platform scheduling setting. Worker Spec definitions remain unchanged.")}</InlineNotice>
          <div className="form-actions"><Button onClick={() => setCreating(false)}>{t("Cancel")}</Button><Button variant="primary" type="submit">{t("Create draft")}</Button></div>
        </form>
      </Modal>
      <Modal open={editing !== null} title={`${t("Edit Spec")} · ${editing?.name || ""}`} description={t("Edit Platform metadata without changing the published business contract.")} onClose={() => setEditing(null)}>
        {editing && <form className="compact-form" onSubmit={saveDescription}><Field name="description" label={t("Description")} value={editing.description} placeholder={t("What this capability provides")} /><label className="field"><span>{t("Execution mode")}</span><select name="execution_mode" defaultValue={editing.execution_mode||"serial"}><option value="serial">{t("Serial")}</option><option value="parallel">{t("Parallel")}</option></select></label><InlineNotice>{t("Changes affect newly submitted tasks only. Queued and running tasks keep their saved classification.")}</InlineNotice><div className="form-actions"><Button onClick={() => setEditing(null)}>{t("Cancel")}</Button><Button variant="primary" type="submit"><Pencil />{t("Save changes")}</Button></div></form>}
      </Modal>
      <Modal
        open={deleting !== null}
        title={`${t("Delete Spec family")} · ${deleting?.name || ""}`}
        description={t("This permanently removes every published version in the Spec family.")}
        onClose={() => {if (!deleteBusy) setDeleting(null);}}
        footer={<><Button disabled={deleteBusy} onClick={() => setDeleting(null)}>{t("Cancel")}</Button><Button variant="danger" disabled={deleteBusy} onClick={() => void removeSpec()}><Trash2 />{t(deleteBusy ? "Deleting…" : "Delete permanently")}</Button></>}
      >
        {deleteError && <InlineNotice tone="error">{deleteError}</InlineNotice>}
        <InlineNotice tone="warning">{t("Historical Tasks and S3 files are retained, but their complete Spec definition can no longer be traced after deletion.")}</InlineNotice>
        {deleting && <div className="delete-summary"><strong>{deleting.name}</strong><span>{deleting.version_count} {t(deleting.version_count === 1 ? "published version" : "published versions")}</span></div>}
      </Modal>
      {selected && <SpecDetailsModal key={`${selected.name}-${selected.enabled}`} spec={selected} onClose={() => setSelected(null)} onToggle={() => void setEnabled(selected)} />}
      {actionSuccess && <InlineNotice tone="success">{actionSuccess}</InlineNotice>}
      {actionError && <InlineNotice tone="error">{actionError}</InlineNotice>}
      {error ? <ErrorState message={error} onRetry={load} /> : loading ? <PageLoading /> : specs.length === 0 ? (
        <Card><EmptyState title={t("Registry is empty")} description={t("Create the first business contract family when a capability is ready to be described.")} /></Card>
      ) : (
        <div className="card-grid">{specs.map((spec) => <Card key={spec.name}><div className="spec-card"><button className="card-primary-action" onClick={() => setSelected(spec)}><div className="spec-card-top"><span className="object-icon object-info"><Braces /></span><span className="status-cluster"><StatusBadge value={spec.state} /><StatusBadge value={spec.enabled ? "enabled" : "disabled"} /></span></div><h3>{spec.name}</h3><small>{t(spec.execution_mode==="parallel"?"Parallel":"Serial")}</small><p>{spec.description || t("No description provided.")}</p><small>{spec.version_count} {t(spec.version_count === 1 ? "published version" : "published versions")}</small></button><div className="card-actions"><Button size="sm" onClick={() => setEditing(spec)}><Pencil />{t("Edit")}</Button><Button size="sm" onClick={() => setSelected(spec)}><Eye />{t("Details")}</Button><Button size="sm" onClick={() => void setEnabled(spec)}>{t(spec.enabled ? "Disable" : "Enable")}</Button><Button size="sm" variant="danger" onClick={() => {setDeleteError(""); setActionSuccess(""); setDeleting(spec);}}><Trash2 />{t("Delete")}</Button></div></div></Card>)}</div>
      )}
    </>
  );
}

function SpecDetailsModal({spec, onClose, onToggle}: {spec: Spec; onClose: () => void; onToggle: () => void}) {
  const {t} = useI18n();
  const {data, error, loading, load} = useLoad<Spec>(`/specs/${encodeURIComponent(spec.name)}`, spec);
  const versions = asList(data.versions);
  const workers = asList(data.workers);
  return (
    <Modal open title={`${t("Spec details")} · ${spec.name}`} description={data.description || t("Immutable JSON contracts and their Worker implementations.")} onClose={onClose}>
      {error ? <ErrorState message={error} onRetry={load} /> : loading ? <PageLoading /> : <div className="detail-stack">
        <div className="detail-header"><div className="status-cluster"><StatusBadge value={data.state} /><StatusBadge value={data.enabled ? "enabled" : "disabled"} /></div><Button size="sm" onClick={onToggle}>{t(data.enabled ? "Disable Spec" : "Enable Spec")}</Button></div>
        {!data.enabled && <InlineNotice tone="warning">{t("Every Worker using this Spec is currently unavailable for new Tasks.")}</InlineNotice>}
        <section className="detail-section"><div className="section-heading"><h3>{t("Published definitions")}</h3><p>{t("Every immutable field and JSON rule published for this Spec.")}</p></div>{versions.length === 0 ? <EmptyState title={t("No published versions")} description={t("A Worker publishes the first validated version.")} /> : versions.map((version) => <div className="spec-version-panel" key={version.version}><div><span><strong>{t("Version")} {version.version}</strong><small>{t("Published from a Worker declaration")} · {t((version.definition as {execution?:unknown})?.execution?"Legacy compatibility (read-only)":"Business contract")}</small></span><code>{version.digest}</code></div><pre className="json-viewer">{JSON.stringify(version.definition, null, 2)}</pre></div>)}</section>
        <section className="detail-section"><div className="section-heading"><h3>{t("Workers using this Spec")}</h3><p>{t("Workers across every Manager that declare this contract family.")}</p></div>{workers.length === 0 ? <EmptyState title={t("No Workers use this Spec")} description={t("Worker declarations will appear here after connection.")} /> : <TableFrame><table><thead><tr><th>{t("Worker")}</th><th>{t("Manager")}</th><th>{t("Version")}</th><th>{t("Runtime")}</th><th>{t("Status")}</th></tr></thead><tbody>{workers.map((worker) => <tr key={worker.id}><td><strong>{worker.name}</strong><small><code>{worker.id}</code></small></td><td>{worker.manager_name || worker.manager_id}</td><td>{worker.active_spec_version || "—"}</td><td>{worker.runtime_version || "—"}</td><td><StatusBadge value={Boolean(worker.effective_enabled) ? "enabled" : "disabled"} /></td></tr>)}</tbody></table></TableFrame>}</section>
      </div>}
    </Modal>
  );
}

function Controllers() {
  const {t} = useI18n();
  const packageInputRef = useRef<HTMLInputElement>(null);
  const {data, error, loading, load} = useLoad<{items: Controller[] | null}>("/controllers", {items: []});
  const controllers = asList(data?.items);
  const [actionError, setActionError] = useState("");
  const [controllerPackage, setControllerPackage] = useState<File | null>(null);
  const [installing, setInstalling] = useState(false);
  const [selected, setSelected] = useState<Controller | null>(null);
  const [editing, setEditing] = useState<Controller | null>(null);
  const [deleting, setDeleting] = useState<Controller | null>(null);

  function notifyControllers() {
    dispatchEvent(new Event("layla:controllers-changed"));
  }

  async function install() {
    if (!controllerPackage) return;
    setActionError("");
    setInstalling(true);
    const form = new FormData();
    form.set("package", controllerPackage);
    try {
      await api("/controllers/install", {method: "POST", body: form});
      setControllerPackage(null);
      if (packageInputRef.current) packageInputRef.current.value = "";
      await load();
      notifyControllers();
    } catch (requestError) {
      setActionError(errorMessage(requestError));
    } finally {
      setInstalling(false);
    }
  }

  async function setEnabled(controller: Controller) {
    setActionError("");
    try {
      await patch(`/controllers/${controller.id}`, {enabled: !Boolean(controller.enabled)});
      await load();
      notifyControllers();
      if (selected?.id === controller.id) setSelected({...selected, enabled: !Boolean(controller.enabled)});
    } catch (requestError) {
      if (!Boolean(controller.enabled) && requestError instanceof ApiError && requestError.code === "CONFLICT") {
        setActionError(t("Controller enablement rechecked the Registry, but no declared version matches a published Spec yet."));
        await load();
      } else {
        setActionError(errorMessage(requestError));
      }
    }
  }

  async function saveMetadata(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!editing) return;
    setActionError("");
    const form = new FormData(event.currentTarget);
    const alias = String(form.get("alias") || "");
    const description = String(form.get("description") || "");
    try {
      await patch(`/controllers/${editing.id}`, {alias, description});
      if (selected?.id === editing.id) setSelected({...selected, alias: alias.trim(), description: description.trim()});
      setEditing(null);
      await load();
      notifyControllers();
    } catch (requestError) {
      setActionError(errorMessage(requestError));
    }
  }

  async function removeController() {
    if (!deleting) return;
    setActionError("");
    try {
      await api(`/controllers/${encodeURIComponent(deleting.id)}`, {method: "DELETE"});
      if (selected?.id === deleting.id) setSelected(null);
      setDeleting(null);
      await load();
      notifyControllers();
    } catch (requestError) {
      setActionError(errorMessage(requestError));
    }
  }

  return (
    <>
      <PageHeader title={t("Controllers")} description={t("Validate and activate independent UI packages against Registry contracts.")} actions={<><RefreshButton onClick={load} /><input ref={packageInputRef} className="visually-hidden" type="file" accept=".zip" onChange={(event) => setControllerPackage(event.target.files?.[0] || null)} /><Button variant="primary" onClick={() => packageInputRef.current?.click()}><UploadCloud />{t("Upload Controller UI")}</Button></>} />
      <Modal
        open={controllerPackage !== null}
        title={t("Validate and install Controller UI")}
        description={t("Review the selected ZIP package before Platform validates its manifest and Spec declarations.")}
        onClose={() => setControllerPackage(null)}
        footer={<><Button onClick={() => setControllerPackage(null)}>{t("Cancel")}</Button><Button variant="primary" disabled={installing} onClick={() => void install()}><UploadCloud />{t(installing ? "Installing…" : "Validate and install")}</Button></>}
      >
        {controllerPackage && <div className="delete-summary"><strong>{controllerPackage.name}</strong><span>{formatBytes(controllerPackage.size)} · {t("ZIP packages up to 64 MB")}</span><Button size="sm" onClick={() => packageInputRef.current?.click()}>{t("Choose another file")}</Button></div>}
      </Modal>
      {selected && (
        <ControllerDetailsModal
          key={`${selected.id}-${selected.enabled}`}
          controller={selected}
          onClose={() => setSelected(null)}
          onToggle={() => void setEnabled(selected)}
          onOpen={() => navigate("controller-app", selected.id)}
        />
      )}
      <Modal open={editing !== null} title={`${t("Edit Controller")} · ${editing?.name || ""}`} description={t("Edit descriptive metadata without changing the installed package or Spec contract.")} onClose={() => setEditing(null)}>
        {editing && <form className="compact-form" onSubmit={saveMetadata}>
          <Field name="alias" label={t("Sidebar alias")} value={editing.alias || ""} placeholder={editing.name} />
          <label className="field"><span>{t("Description")}</span><textarea name="description" defaultValue={editing.description || ""} maxLength={1000} placeholder={t("What this Controller provides")} /></label>
          <InlineNotice>{t("The alias only changes this Controller's label in the Functions section.")}</InlineNotice>
          <div className="form-actions"><Button onClick={() => setEditing(null)}>{t("Cancel")}</Button><Button variant="primary" type="submit"><Pencil />{t("Save changes")}</Button></div>
        </form>}
      </Modal>
      <Modal
        open={deleting !== null}
        title={t("Delete Controller")}
        description={t("This removes the installed UI package. Existing Task history is retained.")}
        onClose={() => setDeleting(null)}
        footer={<><Button onClick={() => setDeleting(null)}>{t("Cancel")}</Button><Button variant="danger" onClick={() => void removeController()}><Trash2 />{t("Delete permanently")}</Button></>}
      >
        <InlineNotice tone="warning">{t("The Controller will disappear from management and the sidebar. This action cannot restore its UI package.")}</InlineNotice>
        {deleting && <div className="delete-summary"><strong>{deleting.name}</strong><code>{deleting.id}</code></div>}
      </Modal>
      {actionError && <InlineNotice tone="error">{actionError}</InlineNotice>}
      {error ? <ErrorState message={error} onRetry={load} /> : loading ? <PageLoading /> : controllers.length === 0 ? (
        <Card><EmptyState title={t("No Controllers installed")} description={t("Installed Controller packages will be validated and listed here.")} /></Card>
      ) : (
        <TableFrame><table><thead><tr><th>{t("Controller")}</th><th>{t("Spec")}</th><th>{t("Validation")}</th><th>{t("Active version")}</th><th>{t("Status")}</th><th className="align-right">{t("Actions")}</th></tr></thead><tbody>{controllers.map((controller) => <tr key={controller.id}><td><button className="table-primary-action" onClick={() => setSelected(controller)}><strong>{controller.name}</strong><small>v{controller.controller_version} · <code>{controller.id}</code></small></button></td><td><strong>{controller.spec_name}</strong><small><StatusBadge value={Boolean(controller.spec_enabled) ? "enabled" : "disabled"} /></small></td><td><StatusBadge value={controller.validation_state} /></td><td><strong>{controller.active_spec_version || "—"}</strong><small>{t("Selected automatically")}</small></td><td><StatusBadge value={Boolean(controller.enabled) ? "enabled" : "disabled"} /></td><td className="align-right"><div className="manager-actions"><Button size="sm" onClick={() => setSelected(controller)}><Eye />{t("Details")}</Button><a className="ui-button button-outline button-sm" href={`/api/ui/v1/controllers/${encodeURIComponent(controller.id)}/package`} download><Download />{t("Download ZIP")}</a><Button size="sm" onClick={() => setEditing(controller)}><Pencil />{t("Edit")}</Button><Button size="sm" onClick={() => void setEnabled(controller)}>{t(Boolean(controller.enabled) ? "Disable" : "Enable")}</Button>{Boolean(controller.enabled) && controller.active_spec_version && Boolean(controller.spec_enabled) && <Button size="sm" onClick={() => navigate("controller-app", controller.id)}>{t("Open")}<ChevronRight /></Button>}<Button size="sm" variant="danger" onClick={() => setDeleting(controller)}><Trash2 />{t("Delete")}</Button></div></td></tr>)}</tbody></table></TableFrame>
      )}
    </>
  );
}

function ControllerDetailsModal({controller, onClose, onToggle, onOpen}: {controller: Controller; onClose: () => void; onToggle: () => void; onOpen: () => void}) {
  const {t} = useI18n();
  const {data, error, loading, load} = useLoad<Controller>(`/controllers/${encodeURIComponent(controller.id)}`, controller);
  const supported = asList(data.supported_specs);
  const workers = asList(data.workers);
  const interfaces = asList(data.interfaces);
  const runnable = Boolean(data.enabled) && Boolean(data.spec_enabled) && data.validation_state === "accepted" && Boolean(data.active_spec_version);
  return (
    <Modal open title={`${t("Controller details")} · ${controller.name}`} description={t("Inspect the Controller package, public contracts, compatible Workers, and Platform interfaces.")} onClose={onClose}>
      {error ? <ErrorState message={error} onRetry={load} /> : loading ? <PageLoading /> : <div className="detail-stack">
        <div className="detail-header"><div className="status-cluster"><StatusBadge value={data.validation_state} /><StatusBadge value={Boolean(data.enabled) ? "enabled" : "disabled"} /></div><div className="manager-actions"><a className="ui-button button-outline button-sm" href={`/api/ui/v1/controllers/${encodeURIComponent(data.id)}/package`} download><Download />{t("Download ZIP")}</a><Button size="sm" onClick={onToggle}>{t(Boolean(data.enabled) ? "Disable" : "Enable")}</Button>{runnable && <Button size="sm" variant="primary" onClick={onOpen}>{t("Open in Platform")}<ChevronRight /></Button>}</div></div>
        {!Boolean(data.spec_enabled) && <InlineNotice tone="warning">{t("This Controller cannot run because its Spec is disabled.")}</InlineNotice>}
        <div className="key-value-list detail-grid"><InfoRow label={t("Controller ID")} value={data.id} /><InfoRow label={t("Controller version")} value={data.controller_version} /><InfoRow label={t("Spec")} value={`${data.spec_name} · ${data.active_spec_version || "—"}`} /><InfoRow label={t("Created")} value={formatDate(data.created_at)} /><InfoRow label={t("Sidebar alias")} value={data.alias || data.name} /><InfoRow label={t("Description")} value={data.description || t("No description provided.")} /></div>
        <section className="detail-section"><div className="section-heading"><h3>{t("Controller manifest")}</h3><p>{t("The validated package metadata used by Platform.")}</p></div><pre className="json-viewer">{JSON.stringify(data.manifest ?? {}, null, 2)}</pre></section>
        <section className="detail-section"><div className="section-heading"><h3>{t("Supported Specs")}</h3><p>{t("Immutable contract versions declared by this Controller.")}</p></div>{supported.length === 0 ? <EmptyState title={t("No supported Specs declared")} description={t("A valid Controller package must declare at least one contract version.")} /> : <TableFrame><table><thead><tr><th>{t("Spec")}</th><th>{t("Version")}</th><th>{t("Digest")}</th><th>{t("Validation")}</th></tr></thead><tbody>{supported.map((spec) => <tr key={`${spec.spec_name}-${spec.version}`}><td>{spec.spec_name}</td><td>{spec.version}</td><td><code>{spec.digest}</code></td><td><StatusBadge value={spec.validation_state} /></td></tr>)}</tbody></table></TableFrame>}</section>
        <section className="detail-section"><div className="section-heading"><h3>{t("Usable Workers")}</h3><p>{t("Workers with a matching accepted Spec digest across all Managers.")}</p></div>{workers.length === 0 ? <EmptyState title={t("No compatible Workers")} description={t("Connect an enabled Worker that publishes the same Spec version and digest.")} /> : <TableFrame><table><thead><tr><th>{t("Worker")}</th><th>{t("Manager")}</th><th>{t("Version")}</th><th>{t("Runtime")}</th><th>{t("Availability")}</th></tr></thead><tbody>{workers.map((worker) => <tr key={worker.id}><td><strong>{worker.name}</strong><small><code>{worker.id}</code></small></td><td>{worker.manager_name || worker.manager_id}</td><td>{worker.active_spec_version || "—"}</td><td>{worker.runtime_version || t("Not reported")}</td><td><StatusBadge value={Boolean(worker.available) ? "ready" : "disabled"} /></td></tr>)}</tbody></table></TableFrame>}</section>
        <section className="detail-section"><div className="section-heading"><h3>{t("Platform interfaces")}</h3><p>{t("Public Controller SDK operations exposed through the sandbox bridge.")}</p></div><div className="interface-list">{interfaces.map((item) => <code key={item}>{item}</code>)}</div></section>
      </div>}
    </Modal>
  );
}

function ControllerApplication({controllerID}: {controllerID: string}) {
  const {t} = useI18n();
  const path = controllerID ? `/controllers/${encodeURIComponent(controllerID)}` : "/controllers/missing";
  const {data, error, loading, load} = useLoad<Controller>(path, {id: controllerID, name: t("Controller"), controller_version: "", spec_name: "", enabled: false, validation_state: "pending"});
  const runnable = Boolean(data.enabled) && Boolean(data.spec_enabled) && data.validation_state === "accepted" && Boolean(data.active_spec_version);
  return (
    <>
      <PageHeader title={data.name || t("Controller")} description={t("Controller UI runs inside the Platform workspace through the public SDK bridge.")} actions={<><RefreshButton onClick={load} /><Button onClick={() => navigate("controllers")}>{t("Controller management")}</Button></>} />
      {error ? <ErrorState message={error} onRetry={load} /> : loading ? <PageLoading /> : !runnable ? (
        <Card><EmptyState title={t("Controller is unavailable")} description={t(!Boolean(data.spec_enabled) ? "Its Spec is disabled. Enable the Spec before using this Controller." : "Enable this accepted Controller before opening its function page.")} action={<Button onClick={() => navigate("controllers")}>{t("Open Controller management")}</Button>} /></Card>
      ) : (
        <div className="controller-workspace"><iframe title={data.name} src={`/controllers/${encodeURIComponent(controllerID)}/run?embedded=1`} /></div>
      )}
    </>
  );
}

function GroupedTasks(){
 const [preview,setPreview]=useState<Task|null>(null),[details,setDetails]=useState<Task|null>(null);
 return <><TaskFeed onPreview={setPreview} onDetails={setDetails}/>{preview&&<TaskPreviewModal task={preview} onClose={()=>setPreview(null)}/>} {details&&<TaskDetailsModal task={details} onClose={()=>setDetails(null)}/>}</>;
}

function TelemetryPanel({telemetry, error, loading, onRetry, taskOnly = false}: {telemetry: TelemetryResponse; error: string; loading: boolean; onRetry: () => void; taskOnly?: boolean}) {
  const {t} = useI18n();
  const latest = telemetry.latest;
  const gpus = asList(latest?.gpus);
  const processes = asList(latest?.processes).filter((process) => !taskOnly || process.task_process);
  const ports = asList(latest?.ports);
  const resources = latest?.task_resources;
  if (error) return <section className="detail-section"><ErrorState message={error} onRetry={onRetry} /></section>;
  if (loading) return <section className="detail-section"><PageLoading /></section>;
  if (!latest) return <section className="detail-section"><EmptyState title={t("No telemetry reported")} description={t("Telemetry appears after the updated EManager connects and sends its first sample.")} /></section>;
  return <div className="telemetry-stack">
    <section className="detail-section"><div className="section-heading"><h3>{t(taskOnly ? "Task resource usage" : "Live machine telemetry")}</h3><p>{formatDate(latest.collected_at)} · {telemetry.samples.length} {t("recent samples")}</p></div>
      {latest.active_task ? <InlineNotice tone="info"><strong>{t("Active task")}</strong><code>{latest.active_task.task_id}</code><span>{latest.active_task.worker_id}</span></InlineNotice> : !taskOnly && <InlineNotice>{t("The Manager execution queue is idle.")}</InlineNotice>}
      {resources && <div className="machine-metrics telemetry-resource-grid"><MetricCard icon={Cpu} label={t("Task CPU")} value={formatPercent(resources.cpu_percent)} detail={`${t("Peak")} ${formatPercent(resources.peak_cpu_percent)} · ${resources.process_count || 0} ${t("processes")}`} percent={resources.cpu_percent} /><MetricCard icon={MemoryStick} label={t("Task memory")} value={formatBytes(resources.rss_bytes)} detail={`${t("Peak")} ${formatBytes(resources.peak_rss_bytes)} · ${t("Read")} ${formatBytes(resources.read_bytes)} · ${t("Write")} ${formatBytes(resources.write_bytes)}`} /><MetricCard icon={HardDrive} label={t("Task GPU memory")} value={formatBytes(resources.gpu_memory_bytes)} detail={`${t("Peak")} ${formatBytes(resources.peak_gpu_memory_bytes)}${(resources.pids || []).length ? ` · PID ${(resources.pids || []).join(", ")}` : ""}`} /></div>}
    </section>
    {gpus.length > 0 && <section className="detail-section"><div className="section-heading"><h3>{t("GPU devices")}</h3><p>{t("NVIDIA utilization, memory, temperature, and power reported by EManager.")}</p></div><TableFrame><table><thead><tr><th>{t("GPU")}</th><th>{t("Utilization")}</th><th>{t("GPU memory")}</th><th>{t("Temperature")}</th><th>{t("Power")}</th></tr></thead><tbody>{gpus.map((gpu) => <tr key={gpu.uuid || gpu.index}><td><strong>{gpu.name || `GPU ${gpu.index}`}</strong><small>{gpu.uuid || "—"} · {t("Driver")} {gpu.driver_version || "—"}</small></td><td>{formatPercent(gpu.utilization_percent)}</td><td>{formatBytes(gpu.memory_used_bytes)} / {formatBytes(gpu.memory_total_bytes)}</td><td>{gpu.temperature_c ?? "—"} °C</td><td>{gpu.power_draw_w ?? "—"} / {gpu.power_limit_w ?? "—"} W</td></tr>)}</tbody></table></TableFrame></section>}
    {processes.length > 0 && <section className="detail-section"><div className="section-heading"><h3>{t(taskOnly ? "Task process tree" : "Top processes")}</h3><p>{t("Processes are ordered by resident memory; task processes are retained even outside the top list.")}</p></div><TableFrame><table><thead><tr><th>PID</th><th>{t("Process")}</th><th>CPU</th><th>{t("Memory")}</th><th>{t("Command")}</th></tr></thead><tbody>{processes.slice(0, taskOnly ? 80 : 40).map((process) => <tr key={process.pid}><td><code>{process.pid}</code><small>PPID {process.ppid || 0}</small></td><td><strong>{process.name || process.executable || "—"}</strong><small>{process.worker_id || process.executable || "—"}</small></td><td>{formatPercent(process.cpu_percent)}</td><td>{formatBytes(process.rss_bytes)}</td><td><code className="telemetry-command">{process.command || "—"}</code></td></tr>)}</tbody></table></TableFrame></section>}
    {!taskOnly && ports.length > 0 && <section className="detail-section"><div className="section-heading"><h3>{t("Listening ports")}</h3><p>{t("Current TCP and UDP listeners reported by the host.")}</p></div><TableFrame><table><thead><tr><th>{t("Protocol")}</th><th>{t("Address")}</th><th>{t("Process")}</th></tr></thead><tbody>{ports.slice(0, 100).map((port, index) => <tr key={`${port.protocol}-${port.local_address}-${index}`}><td>{port.protocol || "—"}</td><td><code>{port.local_address || "—"}</code></td><td><code className="telemetry-command">{port.process || "—"}</code></td></tr>)}</tbody></table></TableFrame></section>}
  </div>;
}

function TaskPreviewModal({task, onClose}: {task: Task; onClose: () => void}) {
  const {t} = useI18n();
  const taskID = task.id || task.task_id || "";
  const {data, error, loading, load} = useLoad<Task>(`/tasks/${encodeURIComponent(taskID)}`, task);
  const previewable = [...asList(data.output_artifacts), ...asList(data.input_artifacts)].filter((artifact) => Boolean(artifact.preview_url));
  const [selectedID, setSelectedID] = useState("");
  const selected = previewable.find((artifact) => (artifact.artifact_id || artifact.id) === selectedID) || previewable[0];
  const [markdown, setMarkdown] = useState("");
  const [previewError, setPreviewError] = useState("");
  const kind = selected ? previewKind(selected) : "unsupported";

  useEffect(() => {
    setMarkdown("");
    setPreviewError("");
    if (!selected?.preview_url || kind !== "markdown") return;
    const controller = new AbortController();
    fetch(selected.preview_url, {credentials: "same-origin", signal: controller.signal}).then(async (response) => {
      if (!response.ok) throw new Error(response.statusText);
      setMarkdown(await response.text());
    }).catch((requestError) => {if (requestError?.name !== "AbortError") setPreviewError(errorMessage(requestError));});
    return () => controller.abort();
  }, [selected?.preview_url, kind]);

  return <Modal open title={`${t("Preview")} · ${taskID}`} description={t("Supported S3 files are streamed through Platform without exposing the private object-store address.")} onClose={onClose}>
    {error ? <ErrorState message={error} onRetry={load} /> : loading ? <PageLoading /> : previewable.length === 0 ? <EmptyState title={t("No previewable files")} description={t("Markdown, common image formats, and audio are currently supported. The file may also be missing from S3.")} /> : <div className="task-preview-stack">
      <div className="preview-file-tabs">{previewable.map((artifact) => {const id = artifact.artifact_id || artifact.id || artifact.filename; return <Button key={id} size="sm" variant={selected === artifact ? "primary" : "outline"} onClick={() => setSelectedID(id)}>{artifact.filename}</Button>;})}</div>
      <div className="artifact-preview">{previewError ? <ErrorState message={previewError} /> : kind === "markdown" ? <pre className="markdown-preview">{markdown || t("Loading preview…")}</pre> : kind === "image" ? <img src={selected?.preview_url} alt={selected?.filename || t("Artifact preview")} /> : kind === "audio" ? <audio controls preload="metadata" src={selected?.preview_url}>{t("Your browser cannot play this audio format.")}</audio> : <EmptyState title={t("Preview not supported")} description={t("Download the file to open it with a local application.")} />}</div>
    </div>}
  </Modal>;
}

function previewKind(artifact: Artifact): "markdown" | "image" | "audio" | "unsupported" {
  const type = (artifact.content_type || "").toLowerCase();
  const filename = artifact.filename.toLowerCase();
  if (type.includes("markdown") || filename.endsWith(".md") || filename.endsWith(".markdown")) return "markdown";
  if (type.startsWith("image/")) return "image";
  if (type.startsWith("audio/")) return "audio";
  return "unsupported";
}

function TaskDetailsModal({task, onClose}: {task: Task; onClose: () => void}) {
  const {t} = useI18n();
  const taskID = task.id || task.task_id || "";
  const {data, error, loading, load} = useLoad<Task>(`/tasks/${encodeURIComponent(taskID)}`, task);
  const {data: telemetry, error: telemetryError, loading: telemetryLoading, load: loadTelemetry} = useLoad<TelemetryResponse>(`/tasks/${encodeURIComponent(taskID)}/telemetry`, {latest: null, samples: []});
  const inputArtifacts = asList(data.input_artifacts);
  const outputArtifacts = asList(data.output_artifacts);
  const submittedTask = {
    task_id: data.task_id || data.id || taskID,
    controller_id: data.controller_id,
    target_worker_id: data.target_worker_id,
    spec_name: data.spec_name,
    spec_version: data.spec_version,
    spec_digest: data.spec_digest,
    spec_payload: data.spec_payload,
    scheduling: data.scheduling,
  };
  return (
    <Modal open title={`${t("Task details")} · ${taskID}`} description={t("Review the submitted request, optional S3 inputs, and Worker output. Platform streams user downloads without storing file copies.")} onClose={onClose}>
      {error ? <ErrorState message={error} onRetry={load} /> : loading ? <PageLoading /> : <div className="detail-stack task-detail-stack">
        <div className="detail-header"><div className="status-cluster"><StatusBadge value={data.status} />{data.scheduling&&<span>{t("Submitted classification")}: {t(data.scheduling.mode==="parallel"?"Parallel":"Serial")}</span>}<span>{data.progress_percent || 0}% · {data.progress_message || t("Waiting for progress")}</span></div><span>{formatDate(data.created_at)}</span></div>
        <section className="detail-section"><div className="section-heading"><h3>{t("Submitted Task JSON")}</h3><p>{t("The exact execution envelope and opaque business payload accepted by Platform.")}</p></div><pre className="json-viewer">{JSON.stringify(submittedTask, null, 2)}</pre></section>
        {inputArtifacts.length > 0 && <section className="detail-section"><div className="section-heading"><h3>{t("User input")}</h3><p>{t("Input files remain in S3. Platform securely streams available files to your browser.")}</p></div><ArtifactLinks artifacts={inputArtifacts} /></section>}
        <section className="detail-section"><div className="section-heading"><h3>{t("Worker output")}</h3><p>{t("Structured Worker result and output files produced for this execution.")}</p></div>{data.spec_result !== undefined && data.spec_result !== null ? <pre className="json-viewer">{JSON.stringify(data.spec_result, null, 2)}</pre> : <InlineNotice tone="info">{t("No structured result has been reported yet.")}</InlineNotice>}{outputArtifacts.length > 0 && <ArtifactLinks artifacts={outputArtifacts} />}{data.error_message && <InlineNotice tone="error"><strong>{data.error_code || t("failed")}</strong> · {data.error_message}</InlineNotice>}</section>
        <TelemetryPanel telemetry={telemetry} error={telemetryError} loading={telemetryLoading} onRetry={loadTelemetry} taskOnly />
      </div>}
    </Modal>
  );
}

function ArtifactLinks({artifacts}: {artifacts: Artifact[]}) {
  const {t} = useI18n();
  return <div className="artifact-link-list">{artifacts.map((artifact) => {
    const id = artifact.artifact_id || artifact.id || artifact.filename;
    return <div className="artifact-link-row" key={id}><span><strong>{artifact.filename}</strong><small>{artifact.role} · {formatBytes(artifact.size_bytes)} · {artifact.content_type}</small>{artifact.status === "ready" && !artifact.download_url && <small className="file-missing-note">{t("File not found in the current S3 bucket.")}</small>}</span><StatusBadge value={artifact.status} />{artifact.download_url ? <a className="ui-button button-outline button-sm" href={artifact.download_url} download><Download />{t("Download")}</a> : <Button size="sm" disabled><Download />{t("Download unavailable")}</Button>}</div>;
  })}</div>;
}

function Settings(){const [role,setRole]=useState<string|null>(null);useEffect(()=>{void api<CurrentUser>("/me").then(u=>setRole(u.role));},[]);return role===null?<PageLoading/>:role==="admin"?<Storage/>:<><SystemAccountNav/><ExternalConnections/></>;}
function SettingsSubnav(_props:{active:string}){return <SystemAccountNav/>;}

function Storage() {
  const {t} = useI18n();
  const formRef = useRef<HTMLFormElement>(null);
  const {data: bucketData, error: bucketError, loading: bucketLoading, load: loadBucket} = useLoad<{storage_ready: boolean; items: StorageObject[] | null}>("/storage/objects", {storage_ready: false, items: []});
  const {data: initialization, load: loadInitialization} = useLoad<StorageInitialization>("/storage/initialization", {storage_ready: false, initialized: false, object_prefix: "layla", object_keys: [], read_write_verified: false});
  const {data: storageSettings, loading: settingsLoading, load: loadSettings} = useLoad<StorageSettings>("/settings/storage", {endpoint: "", region: "us-east-1", bucket: "", access_key: "", object_prefix: "layla", path_style: true, secret_configured: false});
  const objects = asList(bucketData?.items);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [confirmInitialization, setConfirmInitialization] = useState(false);
  const [initializing, setInitializing] = useState(false);
  const [testing, setTesting] = useState(false);
  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setMessage("");
    setError("");
    try {
      await put("/settings/storage", Object.fromEntries(new FormData(event.currentTarget)));
      setMessage(t("Storage settings saved and applied."));
      await Promise.all([loadSettings(), loadInitialization(), loadBucket()]);
    } catch (requestError) {
      setError(errorMessage(requestError));
    }
  }
  async function testConnection() {
    if (!formRef.current) return;
    setTesting(true);
    setMessage("");
    setError("");
    try {
      await post("/storage/test", Object.fromEntries(new FormData(formRef.current)));
      setMessage(t("S3 connection and read/write verification passed."));
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setTesting(false);
    }
  }
  async function initialize() {
    setInitializing(true);
    setMessage("");
    setError("");
    try {
      await post<StorageInitialization>("/storage/initialize", {});
      setConfirmInitialization(false);
      setMessage(t("Storage initialized and read/write verification passed."));
      await Promise.all([loadInitialization(), loadBucket()]);
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setInitializing(false);
    }
  }
  return (
    <>
      <PageHeader title={t("Storage")} description={t("Configure the S3-compatible object store used for Artifact transfer.")} />
      <SettingsSubnav active="storage" />
      <Modal
        open={confirmInitialization}
        title={t("Initialize S3 storage")}
        description={t("Create the required object prefixes and verify that Platform can write and read the bucket.")}
        onClose={() => setConfirmInitialization(false)}
        footer={<><Button onClick={() => setConfirmInitialization(false)}>{t("Cancel")}</Button><Button variant="primary" disabled={initializing} onClick={() => void initialize()}><Database />{t(initializing ? "Initializing…" : "Initialize storage")}</Button></>}
      >
        <InlineNotice tone="info">{t("Initialization is idempotent. Existing Task files are not modified or deleted.")}</InlineNotice>
        <div className="delete-summary"><strong>{initialization.object_prefix || "layla"}</strong><span>{t("Only uploads and outputs directory markers are written to S3; Platform state remains in its database.")}</span></div>
      </Modal>
      <div className="settings-grid">
        <Card className="storage-panel" title={t("S3-compatible storage")} description={t("Credentials remain inside Platform and are never exposed to Controllers or Workers.")} action={<Button size="sm" variant="primary" disabled={!initialization.storage_ready} onClick={() => setConfirmInitialization(true)}><Database />{t(initialization.initialized ? "Initialize again" : "Initialize storage")}</Button>}>
          {settingsLoading ? <PageLoading /> : <form ref={formRef} key={`${storageSettings.endpoint}-${storageSettings.bucket}-${storageSettings.access_key}-${storageSettings.object_prefix}`} className="settings-form" onSubmit={save}>
            <Field name="endpoint" label={t("Endpoint")} value={storageSettings.endpoint} placeholder="https://s3.example.com" required />
            <div className="form-two-columns"><Field name="region" label={t("Region")} value={storageSettings.region || "us-east-1"} /><Field name="bucket" label={t("Bucket")} value={storageSettings.bucket} required /></div>
            <div className="form-two-columns"><Field name="access_key" label={t("Access key")} value={storageSettings.access_key} required /><Field name="secret_key" label={t("Secret key")} type="password" placeholder={storageSettings.secret_configured ? t("Saved secret — leave blank to keep it") : ""} required={!storageSettings.secret_configured} autoComplete="new-password" /></div>
            <Field name="object_prefix" label={t("Object prefix")} value={storageSettings.object_prefix || "layla"} />
            {error && <InlineNotice tone="error">{error}</InlineNotice>}
            {message && <InlineNotice tone="success"><Check />{message}</InlineNotice>}
            {initialization.initialized && <div className="setting-note"><span>{t("Initialization status")}</span><strong>{initialization.read_write_verified ? t("Read/write verified") : t("Verification required")}</strong><small>{formatDate(initialization.initialized_at)}</small></div>}
            {initialization.object_keys.length > 0 && <div className="initialization-objects">{initialization.object_keys.map((key) => <code key={key}>{key}</code>)}</div>}
            <InlineNotice tone="warning">{t("Tasks cannot be created, claimed, or started until the configured S3 bucket is reachable.")}</InlineNotice>
            <div className="form-actions"><Button type="button" disabled={testing} onClick={() => void testConnection()}><Cloud />{t(testing ? "Testing…" : "Test connection")}</Button><Button variant="primary" type="submit">{t("Save and apply")}</Button></div>
          </form>}
        </Card>
        <Card className="storage-panel" title={t("Bucket browser")} description={t("Browse every object currently stored in the configured bucket.")} action={<RefreshButton onClick={loadBucket} />}>
          <div className="bucket-browser">
            {bucketLoading ? <div className="bucket-loading">{t("Loading bucket…")}</div> : bucketError || !bucketData.storage_ready ? (
              <EmptyState title={t("Bucket unavailable")} description={t("Configure a reachable S3 bucket and restart Platform before running tasks or browsing files.")} action={<Button onClick={loadBucket}><RefreshCw />{t("Retry")}</Button>} />
            ) : objects.length === 0 ? (
              <EmptyState title={t("Bucket is empty")} description={t("Files written by Controllers and Workers will appear here.")} />
            ) : (
              <StorageObjectTree objects={objects} />
            )}
          </div>
        </Card>
      </div>
    </>
  );
}

type StorageTreeNode = {name: string; path: string; children: StorageTreeNode[]; object?: StorageObject};

function buildStorageTree(objects: StorageObject[]): StorageTreeNode[] {
  const root: StorageTreeNode = {name: "", path: "", children: []};
  for (const object of objects) {
    const parts = object.key.split("/").filter(Boolean);
    let parent = root;
    parts.forEach((part, index) => {
      if (part === ".keep" && index === parts.length - 1) return;
      const path = parts.slice(0, index + 1).join("/");
      let node = parent.children.find((item) => item.name === part);
      if (!node) {
        node = {name: part, path, children: []};
        parent.children.push(node);
      }
      if (index === parts.length - 1) node.object = object;
      parent = node;
    });
  }
  const sort = (nodes: StorageTreeNode[]): StorageTreeNode[] => nodes.sort((a, b) => {
    const aFolder = a.children.length > 0 || !a.object;
    const bFolder = b.children.length > 0 || !b.object;
    return aFolder === bFolder ? a.name.localeCompare(b.name) : aFolder ? -1 : 1;
  }).map((node) => ({...node, children: sort(node.children)}));
  return sort(root.children);
}

function StorageObjectTree({objects}: {objects: StorageObject[]}) {
  const {t} = useI18n();
  const nodes = buildStorageTree(objects);
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set(nodes.map((node) => node.path)));
  const toggle = (path: string) => setExpanded((current) => {
    const next = new Set(current);
    if (next.has(path)) next.delete(path); else next.add(path);
    return next;
  });
  const render = (node: StorageTreeNode, depth: number) => {
    const folder = node.children.length > 0 || !node.object;
    const open = expanded.has(node.path);
    return <div className="storage-tree-node" key={node.path}>
      <div className="storage-tree-row" style={{paddingLeft: `${depth * 1.25 + .35}rem`}}>
        {folder ? <button className="storage-tree-toggle" onClick={() => toggle(node.path)} aria-expanded={open}><ChevronRight className={open ? "tree-chevron-open" : ""} /><FolderOpen /><strong>{node.name}</strong></button> : <><span className="storage-tree-file"><Download /><span><strong>{node.name}</strong><small>{formatBytes(node.object?.size_bytes)} · {formatDate(node.object?.last_modified)}</small></span></span>{node.object?.download_url && <a className="ui-button button-outline button-icon" href={node.object.download_url} download aria-label={`${t("Download")} ${node.path}`} title={t("Download")}><Download /></a>}</>}
      </div>
      {folder && open && node.children.map((child) => render(child, depth + 1))}
    </div>;
  };
  return <div className="storage-tree">{nodes.map((node) => render(node, 0))}</div>;
}

function Authentication() {
  const {t} = useI18n();
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setMessage("");
    setError("");
    const form = new FormData(event.currentTarget);
    try {
      await put("/settings/auth", {enabled: true, issuer: form.get("issuer"), client_id: form.get("client_id"), client_secret: form.get("client_secret"), redirect_url: form.get("redirect_url"), scopes: ["openid", "profile", "email"]});
      setMessage(t("OIDC configuration saved."));
    } catch (requestError) {
      setError(errorMessage(requestError));
    }
  }
  return (
    <>
      <PageHeader title={t("Authentication")} description={t("Connect an OpenID Connect provider while retaining fail-closed validation.")} />
      <SettingsSubnav active="auth" />
      <div className="settings-grid">
        <Card title={t("OIDC provider")} description={t("Authorization Code with PKCE, state, nonce, issuer, and audience checks.")}>
          <form className="settings-form" onSubmit={save}>
            <Field name="issuer" label={t("Issuer")} placeholder="https://identity.example.com" required />
            <Field name="client_id" label={t("Client ID")} required />
            <Field name="client_secret" label={t("Client secret")} type="password" required />
            <Field name="redirect_url" label={t("Redirect URL")} placeholder="https://layla.example.com/api/ui/v1/auth/oidc/callback" required />
            {error && <InlineNotice tone="error">{error}</InlineNotice>}
            {message && <InlineNotice tone="success"><Check />{message}</InlineNotice>}
            <div className="form-actions"><Button variant="primary" type="submit">{t("Save OIDC settings")}</Button></div>
          </form>
        </Card>
        <Card title={t("Security profile")} description={t("Stable security properties applied by Platform.")}>
          <div className="key-value-list"><InfoRow label={t("Flow")} value="Authorization Code + PKCE" /><InfoRow label={t("State and nonce")} value={t("Required")} /><InfoRow label={t("Issuer validation")} value={t("Required")} /><InfoRow label={t("Audience validation")} value={t("Required")} /></div>
          <InlineNotice tone="info">{t("OIDC and local Platform accounts are independent. Enabling OIDC never disables local username and password sign-in.")}</InlineNotice>
        </Card>
      </div>
    </>
  );
}

function ApiDocumentation() {
  const {t} = useI18n();
  const [workflowOpen, setWorkflowOpen] = useState(false);
  const [workerGuideOpen, setWorkerGuideOpen] = useState(false);
  const [controllerGuideOpen, setControllerGuideOpen] = useState(false);
  const [protocolOpen, setProtocolOpen] = useState(() => new URLSearchParams(location.search).get("view") === "protocols");
  const [copied, setCopied] = useState<"worker" | "controller" | "">("");

  useEffect(() => {
    const open = () => setProtocolOpen(true);
    addEventListener("layla:open-protocol", open);
    return () => removeEventListener("layla:open-protocol", open);
  }, []);

  async function copyPrompt(kind: "worker" | "controller", value: string) {
    await navigator.clipboard.writeText(value);
    setCopied(kind);
    window.setTimeout(() => setCopied(""), 1600);
  }
  return (
    <>
      <PageHeader title={t("API Documentation")} description={t("Machine-readable public contracts for independent Manager and Controller implementations.")} />
      <WorkerOperationsGuide />
      <Modal open={workflowOpen} title={t("Platform architecture and execution workflow")} description={t("The organized product rules used to keep Platform, Manager, Worker, and Controller independent.")} onClose={() => setWorkflowOpen(false)}>
        <div className="workflow-list">
          <WorkflowStep number="01" title={t("Platform owns public contracts")} description={t("Platform Core stores identities, immutable Specs, task state, and S3 references. Business payloads remain opaque and no extension-specific logic belongs in Core.")} />
          <WorkflowStep number="02" title={t("Manager represents one machine")} description={t("Exactly one natively deployed Manager coordinates the Workers stored under /opt/Executor/Workers. Manager and Worker remain logically independent over the public protocol.")} />
          <WorkflowStep number="03" title={t("Workers declare compatibility")} description={t("Each Worker owns its runtime version and supported Spec declarations. Platform validates immutable version digests before the Worker becomes available.")} />
          <WorkflowStep number="04" title={t("Controllers stay independent")} description={t("Controller UI packages are uploaded, validated, enabled, and embedded in Platform through the sandboxed public Host SDK. Enabled Controllers appear under Functions.")} />
          <WorkflowStep number="05" title={t("Tasks require reachable S3")} description={t("EManager transfers task files with short-lived S3 authorization inside the trusted network. Public browser downloads are streamed through Platform; core identities and history remain in Platform database.")} />
          <WorkflowStep number="06" title={t("MarkItDown validates the architecture")} description={t("MarkItDown is the first proof that the public Spec, SDK, Manager, and Worker architecture is real. MarkItDown-specific branches in Platform Core or Manager Core are treated as protocol design defects.")} />
        </div>
      </Modal>
      <Modal open={workerGuideOpen} title={t("Develop a Worker")} description={t("Agent-ready rules for implementing a new business Worker against an immutable Spec.")} onClose={() => setWorkerGuideOpen(false)}>
        <div className="workflow-list">
          <WorkflowStep number="01" title={t("Define the immutable Spec")} description={t("Describe payload fields, input Artifact roles, result fields, and output Artifact roles before writing runtime code. Controller and Worker definitions must match exactly.")} />
          <WorkflowStep number="02" title={t("Use the public Worker SDK")} description={t("Connect only to the local EManager endpoint with Worker ID and Manager address. Worker code never receives Platform, Manager Key, S3 credentials, or database access.")} />
          <WorkflowStep number="03" title={t("Own runtime compatibility")} description={t("The Worker reports its runtime version and every supported Spec version during hello. Platform validates the immutable digest and controls effective availability.")} />
          <WorkflowStep number="04" title={t("Respect the workspace boundary")} description={t("Read only declared input paths and write outputs below the supplied output directory. Report relative output paths; EManager performs S3 transfers.")} />
          <WorkflowStep number="05" title={t("Report the full lifecycle")} description={t("Use SDK messages for started, progress, succeeded, and failed. Business payloads and results remain opaque to EManager and Platform.")} />
          <WorkflowStep number="06" title={t("Pass acceptance gates")} description={t("Test malformed payloads, path traversal, reconnect, quarantine, graceful shutdown, dependency failure, and at least one real task before registration.")} />
        </div>
        <div className="agent-prompt-section"><div className="section-heading"><h3>{t("Copyable Agent prompt")}</h3><p>{t("Paste this complete prompt into an Agent task, then append the feature-specific Spec and requirements.")}</p></div><textarea className="agent-prompt" readOnly value={workerAgentPrompt} aria-label={t("Worker Agent prompt")} /><Button variant="primary" onClick={() => void copyPrompt("worker", workerAgentPrompt)}>{copied === "worker" ? <Check /> : <Clipboard />}{t(copied === "worker" ? "Copied" : "Copy Worker prompt")}</Button></div>
      </Modal>
      <Modal open={controllerGuideOpen} title={t("Develop a Controller UI")} description={t("Agent-ready rules for packaging a sandboxed feature UI against the public Host SDK.")} onClose={() => setControllerGuideOpen(false)}>
        <div className="workflow-list">
          <WorkflowStep number="01" title={t("Select the same Spec")} description={t("Use an existing immutable definition or develop it with the Worker. Never create a Controller-only interpretation of the contract.")} />
          <WorkflowStep number="02" title={t("Create the manifest")} description={t("Declare stable identity, Controller version, entry asset, Host SDK protocol version, description, and supported Specs in controller.json.")} />
          <WorkflowStep number="03" title={t("Use only Host SDK operations")} description={t("Obtain context, published Specs, compatible Workers, uploads, tasks, Artifacts, and downloads through the sandbox bridge. Never call EManager, Worker, S3, or internal APIs directly.")} />
          <WorkflowStep number="04" title={t("Build an embedded UI")} description={t("The static UI must render inside Platform's existing page and remain functional under iframe sandbox and Content Security Policy restrictions.")} />
          <WorkflowStep number="05" title={t("Package deterministically")} description={t("Ship controller.json and static assets in one reproducible archive. Reject traversal paths, server executables, credentials, and undeclared external access.")} />
          <WorkflowStep number="06" title={t("Validate before enabling")} description={t("Upload, inspect Spec validation, enable, verify its Functions entry, and run an end-to-end task without adding Controller-specific Core logic.")} />
        </div>
        <div className="agent-prompt-section"><div className="section-heading"><h3>{t("Copyable Agent prompt")}</h3><p>{t("Paste this complete prompt into an Agent task, then append the feature-specific Spec and UI requirements.")}</p></div><textarea className="agent-prompt" readOnly value={controllerAgentPrompt} aria-label={t("Controller Agent prompt")} /><Button variant="primary" onClick={() => void copyPrompt("controller", controllerAgentPrompt)}>{copied === "controller" ? <Check /> : <Clipboard />}{t(copied === "controller" ? "Copied" : "Copy Controller prompt")}</Button></div>
      </Modal>
      <Modal open={protocolOpen} title={t("Platform Protocol v1 profiles")} description={t("The three public boundaries implemented by Platform, EManager, EWorker, and Controller UI.")} onClose={() => {setProtocolOpen(false); history.replaceState({}, "", "/api-docs");}}>
        <div className="workflow-list">
          <WorkflowStep number="01" title={t("Platform ↔ EManager protocol")} description={t("HTTPS with a Platform-issued Manager Key. Covers authenticated heartbeat and host status, expected Worker synchronization, Spec reports, task claim/lifecycle, cancellation delivery, and Artifact transfer authorization.")} />
          <WorkflowStep number="02" title={t("EManager ↔ EWorker Runtime protocol")} description={t("Loopback WebSocket trust boundary. Covers Worker hello and immutable Spec declarations, execution workspace, started/progress/terminal events, cooperative task cancellation, and reconnect/quarantine behavior.")} />
          <WorkflowStep number="03" title={t("Platform ↔ Controller Host SDK")} description={t("Sandboxed iframe and MessageChannel bridge. Provides Controller context, published Spec, compatible Workers, Artifact upload/download, task creation/status/cancel, while preventing direct access to S3 and internal APIs.")} />
        </div>
      </Modal>
      <div className="protocol-summary">
        <Card><ProtocolStat icon={Braces} label={t("Version")} value="Platform Protocol v1" /></Card>
        <Card><ProtocolStat icon={Users} label={t("Profiles")} value="Manager + Controller" /></Card>
        <Card><ProtocolStat icon={ShieldCheck} label={t("Compatibility")} value={t("Additive evolution")} /></Card>
      </div>
      <div className="api-doc-section-heading"><span>{t("Architecture references")}</span><p>{t("Protocol boundaries and product rules maintained by Platform.")}</p></div>
      <Card className="api-doc-card" title={t("Platform architecture and execution workflow")} description={t("Review the product logic, deployment boundaries, and validation rules behind this Platform.")} action={<Button onClick={() => setWorkflowOpen(true)}><Eye />{t("View workflow")}</Button>}>
        <div className="protocol-body"><span className="protocol-mark"><ListChecks /></span><div><p>{t("A concise record of the requirements that govern registration, compatibility, Controller embedding, S3 Artifact transfer, and native execution runtimes.")}</p><Button variant="primary" onClick={() => setWorkflowOpen(true)}>{t("Open requirements")}</Button></div></div>
      </Card>
      <Card className="api-doc-card" title={t("Protocol v1 profiles")} description={t("Inspect the Manager, Worker runtime, and Controller Host SDK boundaries behind the top-right protocol button.")} action={<Button onClick={() => setProtocolOpen(true)}><CircleGauge />{t("View protocols")}</Button>}>
        <div className="protocol-body"><span className="protocol-mark"><CircleGauge /></span><div><p>{t("Each profile is independently versioned at the shared public architecture boundary; business-specific behavior remains outside Platform and EManager Core.")}</p><Button variant="primary" onClick={() => setProtocolOpen(true)}>{t("Open protocol profiles")}</Button></div></div>
      </Card>
      <div className="api-doc-section-heading"><span>{t("Extension development")}</span><p>{t("Generate a constrained Agent task or inspect the fixed Worker and Controller guides.")}</p></div>
      <DevelopmentPromptBuilder />
      <div className="api-guide-grid">
        <Card title={t("Worker Agent development guide")} description={t("Create a new EWorker adapter that connects to EManager and publishes an immutable Spec.")} action={<Button onClick={() => setWorkerGuideOpen(true)}><Code2 />{t("Open guide")}</Button>}>
          <div className="protocol-body"><span className="protocol-mark"><Cpu /></span><div><p>{t("Covers trust boundaries, Runtime Protocol, workspace files, lifecycle reporting, Spec publication, and acceptance tests.")}</p><Button variant="primary" onClick={() => setWorkerGuideOpen(true)}>{t("Develop a Worker")}</Button></div></div>
        </Card>
        <Card title={t("Controller Agent development guide")} description={t("Create an independently packaged Controller UI using the sandboxed Host SDK.")} action={<Button onClick={() => setControllerGuideOpen(true)}><PanelsTopLeft />{t("Open guide")}</Button>}>
          <div className="protocol-body"><span className="protocol-mark"><PanelsTopLeft /></span><div><p>{t("Covers manifests, shared Specs, Host SDK calls, embedded rendering, deterministic packaging, and validation gates.")}</p><Button variant="primary" onClick={() => setControllerGuideOpen(true)}>{t("Develop a Controller UI")}</Button></div></div>
        </Card>
      </div>
      <div className="api-doc-section-heading"><span>{t("Machine-readable contract")}</span><p>{t("Use this source of truth for public transport structures.")}</p></div>
      <Card title={t("One versioned source of truth")} description={t("Stable transport shapes prevent internal implementation details from leaking across component boundaries.")}>
        <div className="protocol-body"><span className="protocol-mark"><Code2 /></span><div><p>{t("Manager and Controller profiles share IDs, Task, Spec, Artifact, error, timestamp, and correlation structures. Business payloads remain opaque to Platform and Manager.")}</p><a className="ui-button button-primary button-default" href="/api/ui/v1/developer/platform-protocol" target="_blank" rel="noreferrer">{t("Open machine-readable OpenAPI")} <ChevronRight /></a></div></div>
      </Card>
    </>
  );
}

function WorkflowStep({number, title, description}: {number: string; title: string; description: string}) {
  return <section className="workflow-step"><span>{number}</span><div><h3>{title}</h3><p>{description}</p></div></section>;
}

function ProtocolStat({icon: Icon, label, value}: {icon: LucideIcon; label: string; value: string}) {
  return <div className="protocol-stat"><span className="object-icon object-info"><Icon /></span><span><small>{label}</small><strong>{value}</strong></span></div>;
}

function InfoRow({label, value}: {label: string; value: string}) {
  return <div><span>{label}</span><strong>{value}</strong></div>;
}

function Field({
  name,
  label,
  placeholder,
  value,
  type = "text",
  required = false,
  autoComplete,
}: {
  name: string;
  label: string;
  placeholder?: string;
  value?: string;
  type?: string;
  required?: boolean;
  autoComplete?: string;
}) {
  return <label className="field"><span>{label}{required && <b aria-hidden="true">*</b>}</span><input name={name} placeholder={placeholder} defaultValue={value} type={type} required={required} autoComplete={autoComplete} /></label>;
}
